// =========================================================
// client/src/socket.js (로비 네임스페이스 전용)
// =========================================================
import { io } from "socket.io-client";
import { resolveLobbySocketBase } from './config/serviceUrl';
import { EVENTS } from "./protocol/events";
import { MAINTENANCE_SOCKET_EVENTS, dispatchMaintenanceSocketEvent } from "./shared/maintenance/maintenanceBus";
import { MAINTENANCE_MESSAGE_SOCKET_EVENT, dispatchMaintenanceMessages } from "./shared/maintenance/maintenanceMessageBus";
import { OPERATIONS_ANNOUNCEMENT_SOCKET_EVENT, buildOperationsSocketContext, dispatchOperationsAnnouncement } from "./shared/operations/announcementBus";
import { normalizeRecoveryTablesPayload } from "./shared/utils/manualSeatExitLock";
import { ensureFreshAccessToken, getStoredAccessToken, isDefinitiveAuthFailure, refreshAccessToken } from './api/authSession';
import { clearBrowserAuthSessionState } from './shared/utils/authPersistence';

// [ADD][ANCHOR:ROOTSOCKET:RECOVERY_TABLES_CACHE]
// ✅ 로그인 직후 "복구해야 할 테이블 목록"을 받으면
// - 라우터가 아직 마운트되기 전에도 놓치지 않도록 sessionStorage에 캐시
// - 동시에 window CustomEvent로 브로드캐스트해서 어디서든 구독 가능하게 함
const RECOVERY_TABLES_STORAGE_KEY = "npc:recoveryTables";
const RECOVERY_TABLES_CACHE_MAX_AGE_MS = 60_000;
let _lastRecoveryTables = null; // { ts:number, receivedAt:number, tables:[{tableCode, seatNumber?, ...}] }

function _normalizeRecoveryTables(payload) {
  // 서버 payload 형식이 {ts, tables:[...]} 라고 가정하되, 방어적으로 처리
  const tablesRaw = Array.isArray(payload?.tables)
    ? payload.tables
    : Array.isArray(payload)
      ? payload
      : [];

  const tables = tablesRaw
    .map((t) => ({
      ...t,
      tableCode: String(t?.tableCode ?? t?.table_code ?? "").trim(),
      table_code: String(t?.tableCode ?? t?.table_code ?? "").trim(),
      seatNumber: Number(t?.seatNumber ?? t?.seat_number ?? t?.seat ?? 0) || null,
      seat_number: Number(t?.seatNumber ?? t?.seat_number ?? t?.seat ?? 0) || null,
    }))
    .filter((t) => !!t.tableCode);

  let fallbackUserCode = '';
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    fallbackUserCode = String(u?.user_code ?? u?.User_code ?? u?.userCode ?? '').trim();
  } catch (_) {}

  // 명시적으로 일어서기/테이블 나가기를 완료한 테이블은
  // 서버의 지연된 recoveryTables payload가 와도 다시 열지 않는다.
  const payloadUserCode = String(
    payload?.userCode ?? payload?.user_code ?? fallbackUserCode ?? ''
  ).trim();

  return normalizeRecoveryTablesPayload({
    ts: Number(payload?.ts || Date.now()),
    // 서버 ts와 별개로 실제 클라이언트 수신 시각을 저장해 이전 로비 세션 캐시를 구분한다.
    receivedAt: Date.now(),
    userCode: payloadUserCode,
    user_code: payloadUserCode,
    tables,
  }, payloadUserCode);
}

function _cacheRecoveryTables(norm) {
  _lastRecoveryTables = norm;
  try {
    sessionStorage.setItem(RECOVERY_TABLES_STORAGE_KEY, JSON.stringify(norm));
  } catch (_) {}
}

function _broadcastRecoveryTables(norm) {
  try {
    window.dispatchEvent(new CustomEvent("npc:recoveryTables", { detail: norm }));
  } catch (_) {}
}

// [ADD][ANCHOR:ROOTSOCKET:JOINED_TABLE_META_CACHE]
const JOINED_TABLE_META_LS_PREFIX = 'npc:joinedTableMeta:';
const JOINED_TABLE_ACKED_SS_PREFIX = 'npc:joinedTableAck:';
const JOINED_TABLE_META_TTL_MS = 10 * 60 * 1000;

const PENDING_MATCH_SS_PREFIX = 'npc:pendingMatch:';
const JOIN_QUEUE_ACK_TIMEOUT_MS = Math.max(3000, Number(process.env.REACT_APP_JOIN_QUEUE_ACK_TIMEOUT_MS || 8000));
const MATCH_STATUS_TIMEOUT_MS = Math.max(1500, Number(process.env.REACT_APP_MATCH_STATUS_TIMEOUT_MS || 5000));
const MATCH_WAIT_PROMPT_AFTER_MS = Math.max(1000, Number(process.env.REACT_APP_MATCH_WAIT_PROMPT_AFTER_MS || 10_000));
const MATCH_WAIT_TOTAL_MS = Math.max(30_000, Number(process.env.REACT_APP_MATCH_WAIT_TOTAL_MS || 10 * 60 * 1000));

let __matchWaitPromptTimer = null;
let __matchWaitUiState = {
  open: false,
  phase: 'idle',
  request_id: '',
  user_code: '',
  display_code: '',
  queued_at: 0,
  prompt_at: 0,
  cancel_at: 0,
  deadline_at: 0,
  expires_at: 0,
  queue_depth: 0,
  queue_ahead_estimate: 0,
  processing_count: 0,
  delayed_count: 0,
  worker_concurrency: 1,
  queue_sampled_at: 0,
  long_wait_confirmed: false,
};
const __matchWaitUiListeners = new Set();

function _emitMatchWaitUiState(next = {}) {
  __matchWaitUiState = { ...__matchWaitUiState, ...next };
  for (const listener of Array.from(__matchWaitUiListeners)) {
    try { listener({ ...__matchWaitUiState }); } catch (_) {}
  }
  return { ...__matchWaitUiState };
}

function _clearMatchWaitPromptTimer() {
  if (!__matchWaitPromptTimer) return;
  try { clearTimeout(__matchWaitPromptTimer); } catch (_) {}
  __matchWaitPromptTimer = null;
}

function _normalizeMatchWaitMeta(meta = {}) {
  const now = Date.now();
  const queuedAt = Number(meta?.queued_at || meta?.started_at || now) || now;
  const promptAt = Number(meta?.prompt_at || queuedAt + MATCH_WAIT_PROMPT_AFTER_MS) || (queuedAt + MATCH_WAIT_PROMPT_AFTER_MS);
  const expiresAt = Number(meta?.expires_at || meta?.deadline_at || queuedAt + MATCH_WAIT_TOTAL_MS) || (queuedAt + MATCH_WAIT_TOTAL_MS);
  return {
    request_id: String(meta?.request_id || meta?.requestId || '').trim(),
    user_code: String(meta?.user_code || meta?.userCode || _currentJoinedTableUserCode() || '').trim(),
    display_code: String(meta?.display_code || meta?.displayCode || '').trim(),
    queued_at: queuedAt,
    prompt_at: promptAt,
    cancel_at: Number(meta?.cancel_at || Math.max(queuedAt, expiresAt - 10_000)) || Math.max(queuedAt, expiresAt - 10_000),
    deadline_at: Number(meta?.deadline_at || expiresAt) || expiresAt,
    expires_at: expiresAt,
    queue_depth: Math.max(0, Number(meta?.queue_depth || 0)),
    queue_ahead_estimate: Math.max(0, Number(meta?.queue_ahead_estimate ?? meta?.queue_ahead ?? 0)),
    processing_count: Math.max(0, Number(meta?.processing_count || 0)),
    delayed_count: Math.max(0, Number(meta?.delayed_count || 0)),
    worker_concurrency: Math.max(1, Number(meta?.worker_concurrency || 1)),
    queue_sampled_at: Math.max(0, Number(meta?.queue_sampled_at || meta?.sampled_at || 0)),
    long_wait_confirmed: meta?.long_wait_confirmed === true,
  };
}

function _scheduleMatchWaitPrompt(meta = {}) {
  const normalized = _normalizeMatchWaitMeta(meta);
  if (!normalized.request_id) return null;
  _clearMatchWaitPromptTimer();
  if (normalized.long_wait_confirmed) {
    _emitMatchWaitUiState({ ...normalized, open: true, phase: 'waiting' });
    return normalized;
  }
  _emitMatchWaitUiState({ ...normalized, open: false, phase: 'counting' });
  const openPrompt = () => {
    if (__matchWaitUiState.request_id !== normalized.request_id) return;
    if (Date.now() >= normalized.expires_at) return;
    _emitMatchWaitUiState({ ...normalized, open: true, phase: 'prompt' });
  };
  const delay = Math.max(0, normalized.prompt_at - Date.now());
  if (delay <= 0) openPrompt();
  else {
    __matchWaitPromptTimer = setTimeout(openPrompt, delay);
  }
  return normalized;
}

export function subscribeMatchWaitUi(listener) {
  if (typeof listener !== 'function') return () => {};
  __matchWaitUiListeners.add(listener);
  try { listener({ ...__matchWaitUiState }); } catch (_) {}
  return () => __matchWaitUiListeners.delete(listener);
}

export function getMatchWaitUiState() {
  return { ...__matchWaitUiState };
}

export function resumeMatchWaitUi(meta = {}) {
  const normalized = _normalizeMatchWaitMeta(meta);
  if (!normalized.request_id) return null;
  if (Date.now() >= normalized.expires_at) {
    closeMatchWaitUi(normalized.request_id);
    return null;
  }
  return _scheduleMatchWaitPrompt(normalized);
}

export function closeMatchWaitUi(requestId = '') {
  const rid = String(requestId || '').trim();
  if (rid && __matchWaitUiState.request_id && rid !== __matchWaitUiState.request_id) return false;
  _clearMatchWaitPromptTimer();
  _emitMatchWaitUiState({
    open: false,
    phase: 'idle',
    request_id: '',
    user_code: '',
    display_code: '',
    queued_at: 0,
    prompt_at: 0,
    cancel_at: 0,
    deadline_at: 0,
    expires_at: 0,
    queue_depth: 0,
    queue_ahead_estimate: 0,
    processing_count: 0,
    delayed_count: 0,
    worker_concurrency: 1,
    queue_sampled_at: 0,
    long_wait_confirmed: false,
  });
  return true;
}

function _handleTerminalMatchUi(payload = {}) {
  const requestId = String(payload?.request_id || payload?.requestId || '').trim();
  closeMatchWaitUi(requestId);
  clearPendingMatchRequest(requestId, payload?.user_code || payload?.userCode);
}

function _pendingMatchStorageKey(uc = '') {
  const code = String(uc || _currentJoinedTableUserCode() || '').trim();
  return code ? `${PENDING_MATCH_SS_PREFIX}${code}` : '';
}

export function createMatchRequestId() {
  const randomPart = (() => {
    try {
      if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID().replace(/-/g, '');
    } catch (_) {}
    return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  })();
  return `cli_${Date.now().toString(36)}_${randomPart}`.slice(0, 124);
}

export function savePendingMatchRequest(payload = {}) {
  const user_code = String(payload?.user_code ?? payload?.userCode ?? _currentJoinedTableUserCode() ?? '').trim();
  const request_id = String(payload?.request_id ?? payload?.requestId ?? '').trim();
  const display_code = String(payload?.display_code ?? payload?.displayCode ?? '').trim();
  const key = _pendingMatchStorageKey(user_code);
  if (!key || !request_id) return null;
  const body = {
    ...payload,
    request_id,
    user_code,
    display_code,
    started_at: Number(payload?.started_at || Date.now()),
    updated_at: Date.now(),
  };
  try { sessionStorage.setItem(key, JSON.stringify(body)); } catch (_) {}
  return body;
}

export function getPendingMatchRequest(uc = '') {
  const key = _pendingMatchStorageKey(uc);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.request_id) return null;
    return parsed;
  } catch (_) { return null; }
}

export function clearPendingMatchRequest(requestId = '', uc = '') {
  const key = _pendingMatchStorageKey(uc);
  if (!key) return false;
  try {
    const current = getPendingMatchRequest(uc);
    const rid = String(requestId || '').trim();
    if (rid && current?.request_id && String(current.request_id) !== rid) return false;
    sessionStorage.removeItem(key);
    return true;
  } catch (_) { return false; }
}

function _getJoinedTableAckKey(requestId = '') {
  const rid = String(requestId || '').trim();
  return rid ? `${JOINED_TABLE_ACKED_SS_PREFIX}${rid}` : '';
}

function _hasAckedJoinedTable(requestId = '') {
  const key = _getJoinedTableAckKey(requestId);
  if (!key) return false;
  try { return sessionStorage.getItem(key) === '1'; } catch (_) { return false; }
}

function _markAckedJoinedTable(requestId = '') {
  const key = _getJoinedTableAckKey(requestId);
  if (!key) return false;
  try { sessionStorage.setItem(key, '1'); return true; } catch (_) { return false; }
}

function _currentJoinedTableUserCode() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return String(u?.user_code ?? u?.User_code ?? u?.userCode ?? userCode ?? '').trim();
  } catch (_) {
    return String(userCode ?? '').trim();
  }
}

function _validateJoinedTablePayload(payload = {}) {
  const requestId = String(payload?.request_id ?? payload?.requestId ?? '').trim();
  const payloadUser = String(payload?.user_code ?? payload?.userCode ?? '').trim();
  const currentUser = _currentJoinedTableUserCode();
  if (payloadUser && currentUser && payloadUser !== currentUser) {
    return { ok: false, request_id: requestId, reason: 'user_mismatch', payload_user_code: payloadUser, current_user_code: currentUser };
  }
  const tableCode = String(payload?.tableCode ?? payload?.table_code ?? '').trim();
  if (!tableCode) return { ok: false, request_id: requestId, reason: 'missing_table_code' };
  return { ok: true, request_id: requestId };
}

function _ackJoinedTable(ack, response = {}) {
  if (typeof ack !== 'function') return false;
  try { ack(response); return true; } catch (_) { return false; }
}

function _normalizeJoinedTableMeta(payload = {}) {
  const tableCode = String(payload?.tableCode ?? payload?.table_code ?? '').trim();

  let gameSocketUrl = String(payload?.game_socket_url ?? '').trim();
  gameSocketUrl = gameSocketUrl.replace(/\/+$/, '');
  if (gameSocketUrl.endsWith('/game')) {
    gameSocketUrl = gameSocketUrl.slice(0, -5);
  }

  const gameHost = String(payload?.game_host ?? '').trim();
  const gamePort = Number(payload?.game_port || 0) || null;

  return {
    ...payload,
    tableCode,
    table_code: tableCode,
    server_id: String(payload?.server_id ?? '').trim() || null,
    game_socket_url: gameSocketUrl || null,
    game_host: gameHost || null,
    game_port: gamePort,
    ts: Date.now(),
  };
}

function _joinedTableMetaKey(tableCode = '') {
  const tc = String(tableCode || '').trim();
  return tc ? `${JOINED_TABLE_META_LS_PREFIX}${tc}` : '';
}

function _cacheJoinedTableMeta(payload = {}) {
  const norm = _normalizeJoinedTableMeta(payload);
  if (!norm.tableCode) return norm;
  try {
    localStorage.setItem(_joinedTableMetaKey(norm.tableCode), JSON.stringify(norm));
  } catch (_) {}
  return norm;
}

export function getJoinedTableSocketMeta(tableCode = '') {
  const key = _joinedTableMetaKey(tableCode);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    if (!ts || (Date.now() - ts) > JOINED_TABLE_META_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearJoinedTableSocketMeta(tableCode = '') {
  const key = _joinedTableMetaKey(tableCode);
  if (!key) return false;
  try { localStorage.removeItem(key); return true; } catch (_) { return false; }
}


/* =========================================================
 * URL/파라미터 & 컨텍스트
 * =======================================================*/
const params = new URLSearchParams(window.location.search);
const userCodeParam  = params.get("userCode")  || "";
const nicknameParam  = decodeURIComponent(params.get("nickname") || "");
const tableCodeParam = params.get("tableCode") || "";

export let userCode  = userCodeParam || (JSON.parse(localStorage.getItem("user") || "{}").user_code);
export let tableCode = tableCodeParam || "";
let nickname         = nicknameParam || (JSON.parse(localStorage.getItem("user") || "{}").nickname) || "";
let __authCleared = false;
let token            = (
  localStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("jwt") ||
  localStorage.getItem("authToken") ||
  ""
);

// [PATCH][ANCHOR:ROOTSOCKET:RESOLVE_LOBBY_SOCKET_URL]
// ✅ 개발환경: 프론트(3001/3002)에서 실행 중이어도 로비 root socket은 항상 로비서버(기본 3000)로 연결
// ✅ 운영환경: REACT_APP_LOBBY_SOCKET_URL로 지정 가능
function _resolveLobbySocketBase() {
  return resolveLobbySocketBase();
}

const SOCKET_URL = _resolveLobbySocketBase();

// [ADD][ANCHOR:ROOTSOCKET:DEBUG_WRAPPER]
// ✅ root socket 계열 디버그 로그는 운영/기본 개발 상태에서 OFF
// - development에서도 REACT_APP_ROOT_SOCKET_DEBUG=true 일 때만 출력
const ROOT_SOCKET_DEBUG =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_ROOT_SOCKET_DEBUG === 'true';

const rootSocketDebug = (...args) => {
  if (ROOT_SOCKET_DEBUG) console.log(...args);
};

const rootSocketWarn = (...args) => {
  if (ROOT_SOCKET_DEBUG) console.warn(...args);
};

const rootSocketError = (...args) => {
  if (ROOT_SOCKET_DEBUG) console.error(...args);
};

const AUTH_SOCKET_ERRORS = new Set([
  'SESSION_MISSING',
  'SESSION_EXPIRED',
  'SESSION_MISMATCH',
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_REVOKED',
  'NO_TOKEN',
]);

function _isAuthSocketError(err) {
  const msg = String(err?.message || err?.error || '').trim();
  return AUTH_SOCKET_ERRORS.has(msg);
}

export function clearLocalAuthAndGoLogin(reason = 'session') {
  // 인증을 실제로 종료하는 경로에서는 메모리 auth + 모든 로그인 종속 소켓까지 즉시 폐기한다.
  __authCleared = true;
  token = '';
  userCode = '';
  nickname = '';
  try { _clearRootSessionStoreRetry(); } catch (_) {}
  try { _stopPresenceHeartbeat(); } catch (_) {}
  try { clearRecoverySessionCache({ clearTableHints: true }); } catch (_) {}
  try { clearBrowserAuthSessionState(); } catch (_) {}
  try { rootSocket.auth = { ...(rootSocket.auth || {}), token: '', user_code: '' }; rootSocket.disconnect(); } catch (_) {}
  try { chatSocket.auth = { ...(chatSocket.auth || {}), token: '', user_code: '', userCode: '' }; chatSocket.disconnect(); } catch (_) {}
  try { cashierSocket.auth = { ...(cashierSocket.auth || {}), token: '', user_code: '' }; cashierSocket.disconnect(); } catch (_) {}
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('jwt');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('user_code');
    localStorage.removeItem('userCode');
  } catch (_) {}

  try {
    const path = window.location?.pathname || '';
    if (path !== '/login') {
      window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
    }
  } catch (_) {}
}

rootSocketDebug('[rootSocket] target', SOCKET_URL);

// 공통 옵션
const IO_COMMON = {
  path: "/socket.io",
  transports: ["polling", "websocket"], // 필요에 따라 ["websocket"]만 써도 됨
  reconnection: true,
  timeout: 10000,
};

/* =========================================================
 * Recovery Tables (로그인 직후 복구 테이블 자동 오픈)
 * =======================================================*/
// [ADD][ANCHOR:SOCKET:RECOVERY_TABLE_OPEN_CONSTANTS]
const TABLE_PAGE_PATH = (process.env.REACT_APP_TABLE_PAGE_PATH || '/table').trim() || '/table';
const RECOVERY_LAST_TS_KEY = 'npc:recovery:last_ts';
const RECOVERY_CACHE_KEY   = 'npc:recovery:tables';
const RECOVERY_CUSTOM_EVENT = 'npc:recoveryTables';

// [ADD][ANCHOR:SOCKET:RECOVERY_TABLE_OPEN_HELPERS]
function _safeParseUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function _resolveSessionIdentity() {
  const u = _safeParseUser();
  const uc = String(
    userCode ||
    u.user_code || u.User_code || u.userCode ||
    ''
  ).trim();
  const nn = String(
    nickname ||
    u.nickname || u.nickName ||
    localStorage.getItem('nickname') ||
    ''
  ).trim();
  return { userCode: uc, nickname: nn };
}
function _dedupeRecoveryTables(tables) {
  const arr = Array.isArray(tables) ? tables : [];
  const seen = new Set();
  const out = [];
  for (const t of arr) {
    const code = String(t?.tableCode || t?.table_code || t?.table || '').trim();
    if (!code) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({
      tableCode: code,
      seatNumber: Number(t?.seatNumber ?? t?.seat_number ?? t?.seat ?? 0) || undefined,
    });
  }
  return out;
}
function _buildTablePageUrl({ tableCode, seatNumber }) {
  const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
  const u = new URL('/popup', window.location.origin);
  u.searchParams.set('tableCode', String(tableCode));
  if (seatNumber != null) u.searchParams.set('seatNumber', String(seatNumber));
  if (uc) u.searchParams.set('userCode', uc);
  if (nn) u.searchParams.set('nickname', nn);
  u.searchParams.set('recovery', '1');
  return u.toString();
}
function _dispatchRecoveryTables({ ts, tables }) {
  try {
    window.dispatchEvent(new CustomEvent(RECOVERY_CUSTOM_EVENT, { detail: { ts, tables } }));
  } catch (_) {}
}
function _openRecoveryTable({ tableCode, seatNumber }) {
  // ✅ "팝업만" 허용: 탭(_blank) 폴백 절대 금지
  const url = _buildRecoveryUrl({ tableCode, seatNumber });
  if (!url) return false;
  return _tryOpenPopup(url, `npc_recovery_${String(tableCode || "").trim() || "table"}`);
}

// [ADD][ANCHOR:ROOTSOCKET:IDENTIFY_ONCE_STATE]
// ✅ 같은 연결 세션에서 identify 중복 전송 방지 (disconnect 시 리셋)
let _rootIdentifySent = false;
// [ADD][ANCHOR:ROOTSOCKET:IDENTIFY_SIGNATURE]
// ✅ updateRootContext()에서 같은 컨텍스트로 force identify 연타 방지
let _lastIdentifySig = "";

// [ADD][ANCHOR:ROOTSOCKET:JOINLOBBY_ONCE_STATE]
// ✅ 접속 직후 userJoined 브로드캐스트를 위해 joinLobby는 연결당 1회만
let __rootJoinLobbySent = false;

// [ADD][ANCHOR:ROOTSOCKET:EMIT_IDENTIFY_ONCE]
function _emitRootIdentifyOnce({ force = false, source = '' } = {}) {
  if (!force && _rootIdentifySent) return false;
 const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
 if (!uc) {
   rootSocketWarn(`[rootSocket] ⚠ identify skipped: userCode 없음 (source=${source})`);
   return false;
 }
 const payload = { user_code: uc, table_code: tableCode, nickname: nn || nickname, ...buildOperationsSocketContext() };
  try {
    rootSocket.emit("identify", payload);
    _rootIdentifySent = true;
    rootSocketDebug(`[rootSocket] ▶ identify sent${force ? ' (force)' : ''} (source=${source})`, payload);
    return true;
  } catch (e) {
    rootSocketWarn(`[rootSocket] identify emit failed (source=${source})`, e);
    return false;
  }
}

// [ADD][ANCHOR:ROOTSOCKET:EMIT_JOINLOBBY_ONCE]
function _emitJoinLobbyOnce({ silent = false, source = '' } = {}) {
  if (__rootJoinLobbySent) return false;
  const u = _safeParseUser();
  const id = u?.id ?? u?.ID;
  const uc = String(u?.user_code ?? u?.User_code ?? userCode ?? '').trim();
  if (!uc && id == null) return false;
  try {
    rootSocket.emit('joinLobby', {
      ID: id,
      User_code: uc,
      user_code: uc,
      silent: !!silent, // ✅ 여기서는 silent:false로 브로드캐스트 유도
    });
    __rootJoinLobbySent = true;
    rootSocketDebug(`[rootSocket] ▶ joinLobby sent (silent=${!!silent}) (source=${source})`, { id, uc });
    return true;
  } catch (e) {
    rootSocketWarn(`[rootSocket] joinLobby emit failed (source=${source})`, e);
    return false;
  }
}

// [ADD][ANCHOR:ROOTSOCKET:PRESENCE_HEARTBEAT_CONFIG]
// ✅ 로비 Presence heartbeat
// - 서버 TTL(예: 120초)보다 짧게: 45~60초 권장
const PRESENCE_PING_MS = Number(process.env.REACT_APP_PRESENCE_PING_MS || 60_000);
let __presenceTimer = null;

function _stopPresenceHeartbeat() {
  if (__presenceTimer) {
    clearInterval(__presenceTimer);
    __presenceTimer = null;
  }
}

function _startPresenceHeartbeat() {
  _stopPresenceHeartbeat();
  if (!PRESENCE_PING_MS || PRESENCE_PING_MS < 10_000) return; // 너무 짧은 값 방지
  // 최초 presence 등록은 joinLobby가 담당한다.
  // connect 직후 presencePing을 먼저 보내면 joinLobby의 newlyOnline 판정과 경합할 수 있으므로
  // heartbeat는 첫 interval부터 TTL 갱신만 수행한다.
  __presenceTimer = setInterval(() => {
    try {
      if (!rootSocket?.connected) return;
       const { userCode: uc } = _resolveSessionIdentity();
     if (!uc) return;
     rootSocket.emit('presencePing', { user_code: String(uc) }, () => {});
    } catch (_) {}
  }, PRESENCE_PING_MS);
}

/* =========================================================
 * RootSocket (로비용, 싱글톤)
 * =======================================================*/
const __initialOperationsSocketContext = buildOperationsSocketContext();
export const rootSocket = io(SOCKET_URL, {
  ...IO_COMMON,
  autoConnect: false, // 필요할 때만 연결
  auth: { token, user_code: userCode, language: __initialOperationsSocketContext.language },
  query: { userCode, tableCode, nickname, language: __initialOperationsSocketContext.language },
});

// Socket.IO 재연결 시 서버 room 가입 정보는 사라지므로,
// 지금까지 발견한 display_code를 클라이언트에서 기억했다가 connect마다 다시 가입한다.
const __displaySetSubscriptionCodes = new Set();

// [PATCH][ANCHOR:ROOTSOCKET:SESSION_STORE_RETRY]
// Socket.IO 서버 미들웨어가 SESSION_STORE_UNAVAILABLE로 연결을 거절한 경우에는
// 기본 reconnection 루프가 자동으로 다시 connect하지 않을 수 있다.
// 로그아웃은 하지 않고, 짧은 지수 백오프로 명시적인 socket.connect()를 재시도한다.
const ROOT_SESSION_STORE_RETRY_BASE_MS = Math.max(500, Number(process.env.REACT_APP_SESSION_STORE_RETRY_BASE_MS || 1500) || 1500);
const ROOT_SESSION_STORE_RETRY_MAX_MS = Math.max(ROOT_SESSION_STORE_RETRY_BASE_MS, Number(process.env.REACT_APP_SESSION_STORE_RETRY_MAX_MS || 15000) || 15000);
let __rootSessionStoreRetryTimer = null;
let __rootSessionStoreRetryAttempt = 0;

function _clearRootSessionStoreRetry({ resetAttempt = true } = {}) {
  if (__rootSessionStoreRetryTimer) {
    try { clearTimeout(__rootSessionStoreRetryTimer); } catch (_) {}
    __rootSessionStoreRetryTimer = null;
  }
  if (resetAttempt) __rootSessionStoreRetryAttempt = 0;
}

function _scheduleRootSessionStoreRetry() {
  if (rootSocket?.connected || __rootSessionStoreRetryTimer) return;
  const attempt = Math.min(10, __rootSessionStoreRetryAttempt + 1);
  __rootSessionStoreRetryAttempt = attempt;
  const rawDelay = ROOT_SESSION_STORE_RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt - 1));
  // 다수 클라이언트가 Redis 복구 직후 동시에 몰리는 것을 피하기 위한 소폭 jitter.
  const delay = Math.min(
    ROOT_SESSION_STORE_RETRY_MAX_MS,
    Math.max(500, Math.round(rawDelay * (0.85 + Math.random() * 0.3)))
  );
  rootSocketWarn('[rootSocket] Redis session store unavailable → reconnect scheduled', { attempt, delay });
  __rootSessionStoreRetryTimer = setTimeout(() => {
    __rootSessionStoreRetryTimer = null;
    if (rootSocket?.connected) {
      __rootSessionStoreRetryAttempt = 0;
      return;
    }
    try {
      const latestToken = String(getStoredAccessToken() || token || '').trim();
      if (latestToken) {
        token = latestToken;
        rootSocket.auth = { ...(rootSocket.auth || {}), token: latestToken, user_code: userCode };
      }
      rootSocket.connect();
    } catch (retryError) {
      rootSocketError('[rootSocket] SESSION_STORE_UNAVAILABLE reconnect call failed', retryError);
      _scheduleRootSessionStoreRetry();
    }
  }, delay);
}

function _resubscribeKnownDisplaySets() {
  if (!rootSocket?.connected || __displaySetSubscriptionCodes.size === 0) return;
  const codes = Array.from(__displaySetSubscriptionCodes);
  try {
    rootSocket.emit('subscribeDisplaySets', { display_codes: codes });
    rootSocketDebug('[subscribeDisplaySets:reconnect] ▶', codes);
  } catch (_) {}
}

try { rootSocket.off('matchFailed', _handleTerminalMatchUi); } catch (_) {}
try { rootSocket.off('queueExpired', _handleTerminalMatchUi); } catch (_) {}
rootSocket.on('matchFailed', _handleTerminalMatchUi);
rootSocket.on('queueExpired', _handleTerminalMatchUi);

rootSocket.on("connect", () => {
  _clearRootSessionStoreRetry();
  rootSocketDebug("[rootSocket] 🔗 connected:", rootSocket.id);
  try { window.dispatchEvent(new CustomEvent('npc:rootSocketConnected', { detail: { socket_id: rootSocket.id, ts: Date.now() } })); } catch (_) {}
 // [PATCH][ANCHOR:ROOTSOCKET:CONNECT_IDENTIFY_ONCE]
  _emitRootIdentifyOnce({ source: 'connect' });
  // [ADD][ANCHOR:ROOTSOCKET:CONNECT_JOINLOBBY_ONCE]
  // 최초 presence/newlyOnline 판정은 joinLobby가 먼저 담당한다.
  _emitJoinLobbyOnce({ silent: false, source: 'connect' });
  // [ADD][ANCHOR:ROOTSOCKET:CONNECT_START_PRESENCE_HEARTBEAT]
  // joinLobby와 경합하지 않도록 heartbeat는 이후 TTL 갱신만 수행한다.
  _startPresenceHeartbeat();
  // 재연결된 소켓은 이전 disp:* room을 잃으므로 즉시 복구한다.
  _resubscribeKnownDisplaySets();
});
rootSocket.on("connect_error", async (err) => {
  rootSocketError("[rootSocket] ❌ connect_error:", err);
  const code = String(err?.message || err?.error || '').trim();
  if (code === 'SESSION_STORE_UNAVAILABLE') {
    _scheduleRootSessionStoreRetry();
    return;
  }

  if (code === 'TOKEN_EXPIRED') {
    try {
      const nextToken = await refreshAccessToken();
      token = nextToken;
      rootSocket.auth = { ...(rootSocket.auth || {}), token: nextToken, user_code: userCode };
      if (!rootSocket.connected) rootSocket.connect();
      return;
    } catch (refreshError) {
      if (isDefinitiveAuthFailure(refreshError)) {
        _clearRootSessionStoreRetry();
        clearLocalAuthAndGoLogin(refreshError?.code || code);
        return;
      }

      // [PATCH][ANCHOR:ROOTSOCKET:REFRESH_TRANSIENT_RETRY]
      // Access Token은 만료됐지만 refresh 요청이 Redis/네트워크/5xx 같은
      // 일시 장애로 실패한 경우 로그아웃하지 않는다. 현재 소켓은 서버
      // 미들웨어에서 거절된 상태이므로 기존 지수 백오프 루프를 통해
      // 명시적으로 connect()를 다시 호출해야 복구 후 자동으로 살아난다.
      if (refreshError?.transient === true) {
        rootSocketWarn('[rootSocket] token refresh transient failure → reconnect scheduled', {
          code: String(refreshError?.code || ''),
          status: Number(refreshError?.status || 0),
        });
        _scheduleRootSessionStoreRetry();
      }
      return;
    }
  }

  if (_isAuthSocketError(err)) {
    _clearRootSessionStoreRetry();
    clearLocalAuthAndGoLogin(code || 'socket_auth');
  }
});

if (typeof window !== 'undefined') {
  const syncRootAuthToken = (nextValue = '') => {
    const nextToken = String(nextValue || getStoredAccessToken() || '').trim();
    if (!nextToken) {
      token = '';
      try { rootSocket.auth = { ...(rootSocket.auth || {}), token: '' }; } catch (_) {}
      return;
    }
    __authCleared = false;
    token = nextToken;
    try { rootSocket.auth = { ...(rootSocket.auth || {}), token: nextToken, user_code: userCode }; } catch (_) {}
  };
  window.addEventListener('npc:auth-token-refreshed', (event) => syncRootAuthToken(event?.detail?.token));
  window.addEventListener('npc:auth-force-clear', (event) => {
    clearLocalAuthAndGoLogin(String(event?.detail?.reason || 'session'));
  });
  // 멀티 팝업/멀티테이블 창에서 다른 창이 토큰을 갱신해도 즉시 따라간다.
  window.addEventListener('storage', (event) => {
    if (event?.key === 'token') syncRootAuthToken(event.newValue || '');
  });
}

rootSocket.on('forceDisconnect', (payload = {}) => {
  const reason = String(payload?.reason || 'force_disconnect');
  clearLocalAuthAndGoLogin(reason);
});

rootSocket.on('forceLogout', (payload = {}) => {
  const reason = String(payload?.reason || 'force_logout');
  clearLocalAuthAndGoLogin(reason);
});

// [ADD][ANCHOR:ROOTSOCKET:DUPLICATE_LOGIN_PROBE_ACK]
// 새 로그인 시도 시 서버가 기존 클라이언트가 실제로 살아있는지 확인하는 가벼운 ack 이벤트.
// 이 응답이 돌아오면 서버는 기존 접속이 살아있다고 보고 새 로그인을 차단한다.
rootSocket.on('duplicateLoginProbe', (payload = {}, cb) => {
  try {
    if (__authCleared || !String(getStoredAccessToken() || '').trim()) {
      if (typeof cb === 'function') cb({ ok: false, reason: 'logged_out' });
      return;
    }
    if (typeof cb === 'function') {
      cb({
        ok: true,
        socket_id: rootSocket.id || '',
        request_id: payload?.request_id || '',
        ts: Date.now(),
      });
    }
  } catch (_) {
    try { if (typeof cb === 'function') cb({ ok: false }); } catch (_) {}
  }
});

// [ADD][ANCHOR:ROOTSOCKET:MAINTENANCE_EVENTS]
// Operation Agent -> 기존 서버 -> root socket 경로로 내려오는 점검 이벤트를
// 전역 CustomEvent로 변환하여 로비/테이블 공통 UI가 받을 수 있게 한다.
let __rootMaintenanceBound = false;
function _bindRootMaintenanceEventsOnce() {
  if (__rootMaintenanceBound) return;
  __rootMaintenanceBound = true;
  MAINTENANCE_SOCKET_EVENTS.forEach((eventName) => {
    try {
      rootSocket.on(eventName, (payload = {}) => {
        dispatchMaintenanceSocketEvent(eventName, payload, 'rootSocket');
      });
    } catch (_) {}
  });
}
_bindRootMaintenanceEventsOnce();
try {
  rootSocket.on(OPERATIONS_ANNOUNCEMENT_SOCKET_EVENT, (payload = {}) => {
    dispatchOperationsAnnouncement(payload, 'rootSocket');
  });
} catch (_) {}
try {
  rootSocket.on(MAINTENANCE_MESSAGE_SOCKET_EVENT, (payload = {}) => {
    dispatchMaintenanceMessages(payload, 'rootSocket');
  });
} catch (_) {}

let __operationsLanguageContextBound = false;
if (typeof window !== 'undefined' && !__operationsLanguageContextBound) {
  __operationsLanguageContextBound = true;
  window.addEventListener('APP_LANGUAGE_CHANGED', () => {
    try {
      const context = buildOperationsSocketContext();
      rootSocket.auth = { ...(rootSocket.auth || {}), language: context.language };
      rootSocket.emit('operations:set-context', context);
    } catch (_) {}
  });
}

rootSocket.on("disconnect", (reason) => {
  // [PATCH][ANCHOR:ROOTSOCKET:DISCONNECT_RESET_IDENTIFY]
  // ✅ 재연결 시 다시 identify 보낼 수 있도록 리셋
  _rootIdentifySent = false;
  // [PATCH][ANCHOR:ROOTSOCKET:DISCONNECT_RESET_IDENTIFY_SIG]
  _lastIdentifySig = "";
  // [ADD][ANCHOR:ROOTSOCKET:DISCONNECT_RESET_JOINLOBBY_ONCE]
  __rootJoinLobbySent = false;
  rootSocketWarn("[rootSocket] 🔌 disconnect:", reason);
  // [ADD][ANCHOR:ROOTSOCKET:DISCONNECT_STOP_PRESENCE_HEARTBEAT]
  _stopPresenceHeartbeat();
});


// [ADD][ANCHOR:CHATSOCKET:BOOTSTRAP]
/* =========================================================
 * ChatSocket (backend-chat) : namespace "/chat"
 * - lobby/DM: recent 저장/복구
 * - table: 휘발성(ttlMs=3000)
 * =======================================================*/
const CHAT_E = {
  JOIN:   EVENTS.CHAT_JOIN   || 'chat:join',
  LEAVE:  EVENTS.CHAT_LEAVE  || 'chat:leave',
  SEND:   EVENTS.CHAT_SEND   || 'chat:send',
  RECENT: EVENTS.CHAT_RECENT || 'chat:recent',
  MESSAGE: EVENTS.CHAT_MESSAGE || 'chat:message',
  WARN:    EVENTS.CHAT_WARN    || 'chat:warn',
  LOCKED:  EVENTS.CHAT_LOCKED  || 'chat:locked',
  ERROR:   EVENTS.CHAT_ERROR   || 'chat:error',
  SYSTEM:  EVENTS.CHAT_SYSTEM  || 'chat:system',
};

// [PATCH][ANCHOR:CHATSOCKET:RESOLVE_BASE_SAFE]
// ✅ 채팅 서버 URL이 없으면 게임 화면에서 채팅 소켓을 만들지 않는다.
//    localhost:4010 무한 reconnect가 멀티테이블 게임 소켓/렌더링 부하로 번지는 것을 막는다.
const CHAT_SOCKET_ENV_URL = String(process.env.REACT_APP_CHAT_SOCKET_URL || '').trim().replace(/\/+$/, '');
const CHAT_SOCKET_ENABLED = !!CHAT_SOCKET_ENV_URL || String(process.env.REACT_APP_ENABLE_CHAT_SOCKET || '').toLowerCase() === 'true';
const CHAT_FORCE_PORT = CHAT_SOCKET_ENABLED ? (Number(process.env.REACT_APP_CHAT_FORCE_PORT || 0) || 0) : 0;
let CHAT_SOCKET_BASE = CHAT_SOCKET_ENV_URL;
if (CHAT_SOCKET_ENABLED && !CHAT_SOCKET_BASE) {
  try {
    const u = new URL(window.location.origin);
    if (CHAT_FORCE_PORT > 0) u.port = String(CHAT_FORCE_PORT);
    CHAT_SOCKET_BASE = u.toString().replace(/\/+$/, '');
  } catch (_) {
    const portPart = CHAT_FORCE_PORT > 0 ? `:${CHAT_FORCE_PORT}` : '';
    CHAT_SOCKET_BASE = `${window.location.protocol}//${window.location.hostname}${portPart}`;
  }
}
const CHAT_NAMESPACE = String(process.env.REACT_APP_CHAT_NAMESPACE || '/chat').trim() || '/chat';
function _normalizeNsUrl(base, ns) {
  const b = String(base || '').replace(/\/+$/, '');
  let n = String(ns || '').trim();
  if (!n) n = '/chat';
  if (!b) return '';
  if (!n.startsWith('/')) n = `/${n}`;
  if (b.endsWith(n)) return b;
  return `${b}${n}`;
}
const CHAT_SOCKET_URL = CHAT_SOCKET_ENABLED ? _normalizeNsUrl(CHAT_SOCKET_BASE, CHAT_NAMESPACE) : '';

const __chatInitialIdentity = _resolveSessionIdentity();
const __chatInitialUserCode = __chatInitialIdentity.userCode || userCode || '';
const __chatInitialNickname = __chatInitialIdentity.nickname || nickname || '';

// Chat must survive long mobile background/network gaps. The legacy production
// env still contains REACT_APP_CHAT_RECONNECT_ATTEMPTS=3, which is too small for
// real mobile suspension. Unlimited retry is the safe default; a finite limit is
// only honored when REACT_APP_CHAT_RECONNECT_LIMITED=true is explicitly enabled.
const CHAT_RECONNECT_LIMITED = String(process.env.REACT_APP_CHAT_RECONNECT_LIMITED || '').toLowerCase() === 'true';
const CHAT_RECONNECT_ATTEMPTS = (() => {
  if (!CHAT_RECONNECT_LIMITED) return Infinity;
  const parsed = Number(process.env.REACT_APP_CHAT_RECONNECT_ATTEMPTS || 20);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 20;
})();

export const chatSocket = CHAT_SOCKET_ENABLED && CHAT_SOCKET_URL ? io(CHAT_SOCKET_URL, {
  ...IO_COMMON,
  autoConnect: false,
  reconnectionAttempts: CHAT_RECONNECT_ATTEMPTS,
  reconnectionDelay: Math.max(250, Number(process.env.REACT_APP_CHAT_RECONNECTION_DELAY_MS || 1000)),
  reconnectionDelayMax: Math.max(1000, Number(process.env.REACT_APP_CHAT_RECONNECTION_DELAY_MAX_MS || 10000)),
  randomizationFactor: 0.35,
  auth:  { token, user_code: __chatInitialUserCode, userCode: __chatInitialUserCode, nickname: __chatInitialNickname },
  query: { userCode: __chatInitialUserCode, user_code: __chatInitialUserCode, nickname: __chatInitialNickname },
}) : null;

function _resolveAuthToken() {
  try {
    const u = _safeParseUser();
    return String(
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("jwt") ||
      localStorage.getItem("authToken") ||
      u.accessToken || u.access_token || u.token || u.jwt || u.authToken ||
      token ||
      ""
    ).trim();
  } catch (_) {
    return String(token || "").trim();
  }
}

function _syncChatSocketContext() {
  if (!chatSocket) return;
  try {
    const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
    const tk = _resolveAuthToken();
    if (tk) token = tk;
    chatSocket.auth = { ...(chatSocket.auth || {}), token: tk, user_code: uc, userCode: uc, nickname: nn };
    chatSocket.io.opts.query = { ...(chatSocket.io.opts.query || {}), userCode: uc, user_code: uc, nickname: nn };
  } catch (_) {}
}

let ensureChatPromise = null;
let __chatBound = false;
let __chatStarted = false;
let __chatAuthRecoveryInFlight = false;
const __chatRooms = new Set();
let __chatLobbyJoined = false;

function _emitChatCustomEvent(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch (_) {}
}

function _bindChatSocketOnce() {
  if (!chatSocket) return;
  if (__chatBound) return;
  __chatBound = true;

  chatSocket.on('connect', () => {
    // 최신 컨텍스트 반영
    _syncChatSocketContext();

    // lobby:global 기본 join (1회)
    if (!__chatLobbyJoined) {
      try {
        chatSocket.emit(CHAT_E.JOIN, { roomKey: 'lobby:global' }, (ack) => {
          if (ack?.ok) __chatRooms.add(String(ack.roomKey || 'lobby:global'));
        });
        __chatLobbyJoined = true;
      } catch (_) {}
    }

    // reconnect 시 재-join
    __chatRooms.forEach((rk) => {
      try { chatSocket.emit(CHAT_E.JOIN, { roomKey: rk }, () => {}); } catch (_) {}
    });
  });

  // Socket.IO middleware auth errors do not always re-enter the normal
  // reconnection loop. Refresh an expired access token explicitly and retry.
  chatSocket.on('connect_error', async (err) => {
    const code = String(err?.message || err?.error || '').trim();
    if (code === 'TOKEN_EXPIRED' && !__chatAuthRecoveryInFlight) {
      __chatAuthRecoveryInFlight = true;
      try {
        const nextToken = await refreshAccessToken();
        token = nextToken;
        _syncChatSocketContext();
        if (!chatSocket.connected && !__authCleared) chatSocket.connect();
      } catch (refreshError) {
        if (isDefinitiveAuthFailure(refreshError)) {
          clearLocalAuthAndGoLogin(refreshError?.code || code);
        }
      } finally {
        __chatAuthRecoveryInFlight = false;
      }
      return;
    }

    if (_isAuthSocketError(err)) {
      clearLocalAuthAndGoLogin(code || 'chat_socket_auth');
    }
  });

  chatSocket.on(CHAT_E.MESSAGE, (msg) => _emitChatCustomEvent('npc:chat:message', msg));
  chatSocket.on(CHAT_E.WARN,   (p)   => _emitChatCustomEvent('npc:chat:warn', p));
  chatSocket.on(CHAT_E.LOCKED, (p)   => _emitChatCustomEvent('npc:chat:locked', p));
  chatSocket.on(CHAT_E.ERROR,  (p)   => _emitChatCustomEvent('npc:chat:error', p));
  chatSocket.on(CHAT_E.SYSTEM, (p)   => _emitChatCustomEvent('npc:chat:system', p));
}

export function ensureChatNSReady() {
  if (!chatSocket) return Promise.resolve({ ok: false, disabled: true, error: 'chat_socket_disabled' });
  __chatStarted = true;
  if (ensureChatPromise) return ensureChatPromise;
  ensureChatPromise = new Promise((resolve) => {
    _bindChatSocketOnce();
    _syncChatSocketContext();

    if (chatSocket.connected) {
      ensureChatPromise = null;
      resolve({ ok: true });
      return;
    }

    const timeoutMs = Math.max(1000, Number(process.env.REACT_APP_CHAT_CONNECT_TIMEOUT_MS || 5000));
    let finished = false;
    let timer = null;
    const finish = (result) => {
      if (finished) return;
      finished = true;
      try { chatSocket.off('connect', onConnect); } catch (_) {}
      try { chatSocket.off('connect_error', onError); } catch (_) {}
      if (timer) clearTimeout(timer);
      ensureChatPromise = null;
      resolve(result);
    };
    const onConnect = () => finish({ ok: true });
    const onError = (err) => finish({ ok: false, error: err?.message || 'chat_connect_error' });

    chatSocket.once('connect', onConnect);
    chatSocket.once('connect_error', onError);
    timer = setTimeout(() => finish({ ok: false, error: 'chat_connect_timeout' }), timeoutMs);
    chatSocket.connect();
  });
  return ensureChatPromise;
}

export function buildDmRoomKey(myCode, peerCode) {
  const a = String(myCode || '').trim();
  const b = String(peerCode || '').trim();
  if (!a || !b || a === b) return '';
  const [x, y] = a < b ? [a, b] : [b, a];
  return `dm:${x}:${y}`;
}
export function buildTableRoomKey(tableCode) {
  const tc = String(tableCode || '').trim();
  if (!tc) return '';
  const clean = tc.replace(/^table:/i, '').trim();
  return `table:${clean}`;
}

export async function chatJoinRoom(roomKey) {
  const rk = String(roomKey || '').trim();
  if (!rk) return { ok: false, error: 'invalid roomKey' };
  if (!chatSocket) return { ok: false, disabled: true, error: 'chat_socket_disabled', roomKey: rk };
  const ready = await ensureChatNSReady();
  if (!ready?.ok || !chatSocket.connected) return { ok: false, error: ready?.error || 'chat_not_connected', roomKey: rk };
  return new Promise((resolve) => {
    chatSocket.emit(CHAT_E.JOIN, { roomKey: rk }, (ack) => {
      if (ack?.ok) __chatRooms.add(String(ack.roomKey || rk));
      resolve(ack || { ok: true, roomKey: rk });
    });
  });
}

// [ADD][ANCHOR:CHATSOCKET:EXPORT_LEAVE_ROOM]
export async function chatLeaveRoom(roomKey) {
  const rk = String(roomKey || '').trim();
  if (!rk) return { ok: false, error: 'invalid roomKey' };
  if (!chatSocket) return { ok: false, disabled: true, error: 'chat_socket_disabled', roomKey: rk };
  const ready = await ensureChatNSReady();
  if (!ready?.ok || !chatSocket.connected) return { ok: false, error: ready?.error || 'chat_not_connected', roomKey: rk };
  return new Promise((resolve) => {
    chatSocket.emit(CHAT_E.LEAVE, { roomKey: rk }, (ack) => {
      try { __chatRooms.delete(String(ack?.roomKey || rk)); } catch (_) {}
      resolve(ack || { ok: true, roomKey: rk });
    });
  });
}

export async function chatGetRecent(roomKey, { limit = 50 } = {}) {
  const rk = String(roomKey || '').trim();
  if (!rk) return { ok: false, roomKey: rk, messages: [] };
  if (!chatSocket) return { ok: false, disabled: true, error: 'chat_socket_disabled', roomKey: rk, messages: [] };
  const ready = await ensureChatNSReady();
  if (!ready?.ok || !chatSocket.connected) return { ok: false, error: ready?.error || 'chat_not_connected', roomKey: rk, messages: [] };
  return new Promise((resolve) => {
    chatSocket.emit(CHAT_E.RECENT, { roomKey: rk, limit }, (ack) => {
      const msgs = ack?.messages || ack?.recent || [];
      resolve({
        ...(ack || {}),
        roomKey: ack?.roomKey || rk,
        messages: Array.isArray(msgs) ? msgs : [],
      });
    });
  });
}

export async function chatSendMessage({ roomKey, text, nickname: nn, clientMsgId } = {}) {
  const rk = String(roomKey || '').trim();
  const t  = String(text || '');
  if (!rk) return { ok: false, error: 'invalid roomKey' };
  if (!chatSocket) return { ok: false, disabled: true, error: 'chat_socket_disabled', roomKey: rk };
  const ready = await ensureChatNSReady();
  if (!ready?.ok || !chatSocket.connected) return { ok: false, error: ready?.error || 'chat_not_connected', roomKey: rk };
  return new Promise((resolve) => {
    chatSocket.emit(CHAT_E.SEND, { roomKey: rk, text: t, nickname: nn, clientMsgId }, (ack) => {
      resolve(ack || { ok: true });
    });
  });
}

export function onChatMessage(cb) {
  const handler = (e) => cb?.(e?.detail);
  try { window.addEventListener('npc:chat:message', handler); } catch (_) {}
  return () => { try { window.removeEventListener('npc:chat:message', handler); } catch (_) {} };
}
export function onChatWarn(cb) {
  const handler = (e) => cb?.(e?.detail);
  try { window.addEventListener('npc:chat:warn', handler); } catch (_) {}
  return () => { try { window.removeEventListener('npc:chat:warn', handler); } catch (_) {} };
}
export function onChatLocked(cb) {
  const handler = (e) => cb?.(e?.detail);
  try { window.addEventListener('npc:chat:locked', handler); } catch (_) {}
  return () => { try { window.removeEventListener('npc:chat:locked', handler); } catch (_) {} };
}

// Restore chat after mobile/tab suspension only when the user has actually used
// chat during this page session. This avoids opening an unnecessary second
// socket for every lobby visitor while making active DM/global-chat sessions
// self-heal on foreground/online/pageshow.
export async function resumeChatSocketAfterForeground() {
  if (!chatSocket || !__chatStarted || __authCleared) {
    return { ok: true, skipped: true };
  }

  const latestToken = String(getStoredAccessToken() || '').trim();
  if (!latestToken) return { ok: false, error: 'NO_TOKEN' };
  token = latestToken;
  _syncChatSocketContext();

  if (!chatSocket.connected) {
    return ensureChatNSReady();
  }

  // A long suspended tab can return with the client still reporting connected
  // briefly. Reassert room membership idempotently so DM/global chat does not
  // remain silently stale after network migration or server-side room loss.
  try {
    chatSocket.emit(CHAT_E.JOIN, { roomKey: 'lobby:global' }, (ack) => {
      if (ack?.ok) __chatRooms.add(String(ack.roomKey || 'lobby:global'));
    });
  } catch (_) {}
  __chatRooms.forEach((rk) => {
    try { chatSocket.emit(CHAT_E.JOIN, { roomKey: rk }, () => {}); } catch (_) {}
  });
  return { ok: true, connected: true };
}

// [ADD][ANCHOR:SOCKET:RECOVERY_TABLES_AUTOOPEN]
// ✅ rootSocket 에서 받은 recoveryTables 를 기준으로 "테이블 자동 오픈" + UI 이벤트 브로드캐스트
// - payload 예시: { ts, tables:[{ tableCode, seatNumber, needReconnect:true }...] }
// - TableView의 RecoveryModal이 UI/번역 처리, 여기서는 "열기"만 담당
const RECOVERY_TABLES_EVENT = EVENTS?.RECOVERY_TABLES || "recoveryTables";
const RECOVERY_TABLES_LS_KEY = "npc:recoveryTables";
const RECOVERY_OPENED_SS_KEY = "npc:recoveryOpenedTables"; // 세션 단위 중복 방지

function _persistRecoveryTables(payload) {
  const tables = Array.isArray(payload?.tables) ? payload.tables : [];
  try {
    if (tables.length) localStorage.setItem(RECOVERY_TABLES_LS_KEY, JSON.stringify(payload || {}));
    else localStorage.removeItem(RECOVERY_TABLES_LS_KEY);
  } catch (_) {}
  try {
    if (!tables.length) localStorage.removeItem(RECOVERY_CACHE_KEY);
  } catch (_) {}
}

function _loadOpenedMap() {
  try {
    const raw = sessionStorage.getItem(RECOVERY_OPENED_SS_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch (_) {
    return {};
  }
}

function _markOpened(tableCode) {
  try {
    const tc = String(tableCode || "").trim();
    if (!tc) return;
    const m = _loadOpenedMap();
    m[tc] = Date.now();
    sessionStorage.setItem(RECOVERY_OPENED_SS_KEY, JSON.stringify(m));
  } catch (_) {}
}

function _isOpened(tableCode) {
  const tc = String(tableCode || "").trim();
  if (!tc) return true;
  const m = _loadOpenedMap();
  // ✅ 너무 공격적인 재오픈 방지(1분 내 재오픈 금지)
  const at = Number(m?.[tc] || 0);
  return at > 0 && (Date.now() - at) < 60_000;
}

function _buildRecoveryUrl({ tableCode, seatNumber }) {
  const tc = String(tableCode || "").trim();
  if (!tc) return null;
  const qs = new URLSearchParams();
  qs.set("tableCode", tc);
  if (userCode) qs.set("userCode", String(userCode));
  if (nickname) qs.set("nickname", String(nickname));
  if (seatNumber != null) qs.set("seatNumber", String(seatNumber));
  return `/popup?${qs.toString()}`;
}

// [ADD][ANCHOR:SOCKET:POPUP_RANDOM_NAME_HELPER]
function _rand5() {
  // 00000 ~ 99999
  return String(Math.floor(Math.random() * 100000)).padStart(5, "0");
}

// [ADD][ANCHOR:SOCKET:POPUP_SIZE_BASE_CONST]
// ✅ 팝업 테이블 UI(모바일 스테이지) 기준 크기
const POPUP_BASE_W = 375;
const POPUP_BASE_H = 812;

// [ADD][ANCHOR:SOCKET:POPUP_SCALE_PERSIST]
// ✅ 팝업 스케일은 "한 번" 정해지면 고정(매번 재계산하면 창 크기가 들쭉날쭉해 보임)
const POPUP_SCALE_LS_KEY = 'npc:popupScale';

// [ADD][ANCHOR:SOCKET:POPUP_HELPERS]
function _clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function _calcPopupScale() {
  try {
    const aw = (window.screen?.availWidth ?? window.screen?.width ?? 1200) - 60;
    const ah = (window.screen?.availHeight ?? window.screen?.height ?? 900) - 160;
    const s = Math.min(aw / POPUP_BASE_W, ah / POPUP_BASE_H);
    return _clamp(s, 1, 2.5);
  } catch (_) {
    return 1;
  }
}

function _getPopupScale() {
  try {
    const saved = Number(localStorage.getItem(POPUP_SCALE_LS_KEY));
    if (Number.isFinite(saved) && saved > 0) return _clamp(saved, 1, 2.5);
  } catch (_) {}
  const s = _calcPopupScale();
  try { localStorage.setItem(POPUP_SCALE_LS_KEY, String(s)); } catch (_) {}
  return s;
}

function _appendPopupParams(url, scale) {
  try {
    const u = new URL(url, window.location.origin);
    if (!u.searchParams.get('popup')) u.searchParams.set('popup', '1');
    u.searchParams.set('ps', String(scale));
    return u.toString();
  } catch (_) {
    return url;
  }
}

function _tryOpenPopup(url, name) {
  try {
     // [PATCH][ANCHOR:SOCKET:POPUP_RANDOMIZE_WINNAME]
    // ✅ 같은 name 재사용 방지(브라우저가 기존 창/크기 복원하는 문제 줄임)
    const base = name || "npc_recovery_popup";
    const winName = `${base}_${_rand5()}`;
    // [PATCH][ANCHOR:SOCKET:POPUP_OPEN_AT_TARGET_SIZE]
    // ✅ "UI 스테이지"와 "팝업 innerSize"를 1:1로 맞춘 상태에서 URL 로드
    // - URL 로드 이후 resizeTo를 여러 번 호출하면 "창이 작았다가 커졌다"처럼 보임
    // - about:blank(동일 오리진)에서 먼저 크기를 스냅 → 그 다음 URL 로드
    const scale = _getPopupScale();
    const finalUrl = _appendPopupParams(url, scale);
    const targetInnerW = Math.round(POPUP_BASE_W * scale);
    const targetInnerH = Math.round(POPUP_BASE_H * scale);

    const estOuterW = Math.min((screen.availWidth || 99999) - 20, targetInnerW + 80);
    const estOuterH = Math.min((screen.availHeight || 99999) - 20, targetInnerH + 140);

    const features =
      `popup=yes,noopener,noreferrer,width=${Math.max(300, estOuterW)},height=${Math.max(400, estOuterH)},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`;
      // [PATCH][ANCHOR:SOCKET:POPUP_ONLY_NO_TAB]
    // ✅ "팝업으로만" 열리게 강제:
    // - 일부 브라우저는 popup feature를 줘도 새 탭으로 열 수 있음
    // - 탭으로 열린 것으로 판단되면 즉시 close 하고 실패 처리(자동오픈은 '팝업'만 허용)
    const w = window.open("about:blank", winName, features);
    if (!w) return false;

    // [ADD][ANCHOR:SOCKET:POPUP_SNAP_INNER_SIZE]
    const snapInner = () => {
      try {
        const iw0 = Number(w.innerWidth || 0);
      const ih0 = Number(w.innerHeight || 0);
      // ✅ 팝업 생성 직후 0으로 들어오는 브라우저 방어
      if (!iw0 || !ih0) return;
        const ow = Number(w.outerWidth || 0);
        const oh = Number(w.outerHeight || 0);
        const iw = Number(w.innerWidth || 0);
        const ih = Number(w.innerHeight || 0);
        const dw = ow && iw ? (ow - iw) : 0;
        const dh = oh && ih ? (oh - ih) : 0;

        if (dw > 0 && dh > 0) {
          w.resizeTo(targetInnerW + dw, targetInnerH + dh);
          const dx = targetInnerW - Number(w.innerWidth || 0);
          const dy = targetInnerH - Number(w.innerHeight || 0);
          if (dx || dy) w.resizeBy(dx, dy);
        } else {
          // ✅ dw/dh를 못 읽는 경우에도 targetInner로 끌어가도록 1차 외형 추정 후 dx/dy 보정
        w.resizeTo(estOuterW, estOuterH);
        const dx = targetInnerW - Number(w.innerWidth || 0);
        const dy = targetInnerH - Number(w.innerHeight || 0);
        if (dx || dy) w.resizeBy(dx, dy);
        }
      } catch (_) {}
    };

    snapInner();
  try { w.requestAnimationFrame?.(snapInner); } catch (_) {}
  setTimeout(snapInner, 0);
  setTimeout(snapInner, 50);
  setTimeout(snapInner, 200);
    try {
      const x = Math.max(0, ((screen.availWidth || screen.width || 0) - (w.outerWidth || estOuterW)) / 2);
      const y = Math.max(0, ((screen.availHeight || screen.height || 0) - (w.outerHeight || estOuterH)) / 2);
      w.moveTo?.(x, y);
    } catch (_) {}

    // [PATCH][ANCHOR:SOCKET:POPUP_TAB_DETECT_BY_MISMATCH]
    // ✅ 탭으로 열린 경우: resizeTo가 거의 먹히지 않아서 targetInner와 큰 차이가 남음
    try {
      const iw = Number(w.innerWidth || 0);
      const ih = Number(w.innerHeight || 0);
      const tooDiffW = iw && Math.abs(iw - targetInnerW) > Math.max(240, targetInnerW * 0.25);
      const tooDiffH = ih && Math.abs(ih - targetInnerH) > Math.max(260, targetInnerH * 0.25);
      if (tooDiffW || tooDiffH) {
        try { w.close?.(); } catch (_) {}
        return false;
      }
    } catch (_) {}

    // 여기까지 통과하면 팝업으로 열린 것으로 간주하고 URL 로드
    try { w.location.replace(finalUrl); } catch (_) { try { w.location.href = finalUrl; } catch (_) {} }
    try { w.focus?.(); } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

function _autoOpenRecoveryTables(payload) {
  const list = Array.isArray(payload?.tables) ? payload.tables : [];
  if (!list.length) return;

  // ✅ tableCode dedupe
  const uniq = [];
  const seen = new Set();
  for (const t of list) {
   const tc = String(t?.tableCode || t?.table_code || "").trim();
    if (!tc || seen.has(tc)) continue;
    seen.add(tc);
    uniq.push({ tableCode: tc, seatNumber: t?.seatNumber ?? t?.seat_number });
  }
  if (!uniq.length) return;

  let opened = 0;
  for (const t of uniq) {
    const tc = String(t.tableCode);
    if (_isOpened(tc)) continue;
    const url = _buildRecoveryUrl(t);
    if (!url) continue;

    // ✅ 1) 팝업 시도
    // [PATCH][ANCHOR:SOCKET:POPUP_RANDOMIZE_CALLSITE]
    const ok = _tryOpenPopup(url, `npc_table_${tc}_${_rand5()}`);
    if (ok) {
      _markOpened(tc);
      opened += 1;
      continue;
    }

       // ✅ 2) 팝업이 막히면: 로비/기존 탭을 강제로 이동시키지 않는다.
    // - 복구 테이블은 localStorage + npc:recoveryTables 이벤트로 UI에서 수동 오픈 가능
    if (opened === 0) {
      _markOpened(tc); // 팝업 차단 환경에서 자동 재시도 스팸 방지
      break;
    }
  }
}

function _onRecoveryTables(payload = {}) {
  rootSocketDebug("[rootSocket] ⬅ recoveryTables", payload);

  // ✅ socket.js는 더 이상 복구 테이블 창을 직접 열지 않는다.
  // - 모바일: MobileMultiTableRoot가 기존 Root 내부에서 controller.enterTable()로 화면 전환
  // - 데스크톱: routes/RecoveryAutoOpener가 /popup 창을 1회만 오픈
  // - 여기서는 라우터/Root가 놓치지 않도록 정규화 + 캐시 + 이벤트 브로드캐스트만 담당
  const norm = _normalizeRecoveryTables(payload);
  _persistRecoveryTables(norm);
  _cacheRecoveryTables(norm);
  _broadcastRecoveryTables(norm);
}

// ✅ 중복 바인딩 방지: off 후 on
try { rootSocket.off(RECOVERY_TABLES_EVENT, _onRecoveryTables); } catch (_) {}
rootSocket.on(RECOVERY_TABLES_EVENT, _onRecoveryTables);

// [ADD][ANCHOR:SOCKET:ON_RECOVERY_TABLES_UI_EVENT]
// ✅ (선택) UI에서 recoveryTables를 듣고 싶으면 이걸 쓰면 됨
export function onRecoveryTables(cb) {
  const handler = (e) => cb?.(e?.detail || {});
  try { window.addEventListener("npc:recoveryTables", handler); } catch (_) {}
  return () => {
    try { window.removeEventListener("npc:recoveryTables", handler); } catch (_) {}
  };
}


export async function resumeRootSocketAfterForeground() {
  const latestToken = String(getStoredAccessToken() || '').trim();
  if (!latestToken || __authCleared) return false;

  // Chat recovery must not wait on root reconnect. If the root namespace is
  // temporarily blocked by Redis/session-store recovery, active chat can still
  // restore independently as soon as its endpoint is reachable.
  const chatResume = resumeChatSocketAfterForeground().catch(() => ({ ok: false }));

  token = latestToken;
  rootSocket.auth = { ...(rootSocket.auth || {}), token: latestToken, user_code: userCode };
  if (!rootSocket.connected) await ensureRootNSReady();
  else {
    _emitRootIdentifyOnce({ force: true, source: 'foreground_resume' });
    // 모바일 백그라운드에서 presence TTL이 만료됐더라도 복귀 즉시 다시 등록한다.
    __rootJoinLobbySent = false;
    _emitJoinLobbyOnce({ silent: true, source: 'foreground_resume' });
    _startPresenceHeartbeat();
  }
  await chatResume;
  return true;
}

let ensureRootPromise = null;
const ROOT_SOCKET_READY_TIMEOUT_MS = Math.max(
  2500,
  Number(process.env.REACT_APP_ROOT_SOCKET_READY_TIMEOUT_MS || 6000) || 6000
);

export function ensureRootNSReady() {
  if (rootSocket.connected) {
    _emitRootIdentifyOnce({ source: 'ensureRootNSReady:already_connected' });
    return Promise.resolve(true);
  }
  if (ensureRootPromise) return ensureRootPromise;

  // [FIX][2026-08-16][ANCHOR:SOCKET:ENSURE_ROOT_TIMEOUT]
  // 서버/네트워크 장애 때 connect 이벤트가 영원히 오지 않아 bootstrap UI까지
  // 무한 대기하던 문제를 막는다. Socket.IO 자체 재연결은 그대로 계속 동작한다.
  ensureRootPromise = new Promise((resolve, reject) => {
    let settled = false;
    let timer = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      try { rootSocket.off('connect', onConnect); } catch (_) {}
      ensureRootPromise = null;
    };

    const finish = (ok, error = null) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (ok) resolve(true);
      else reject(error || new Error('ROOT_SOCKET_READY_TIMEOUT'));
    };

    const onConnect = () => {
      _emitRootIdentifyOnce({ source: 'ensureRootNSReady' });
      finish(true);
    };

    rootSocket.once('connect', onConnect);
    timer = setTimeout(() => {
      const error = new Error(`ROOT_SOCKET_READY_TIMEOUT:${ROOT_SOCKET_READY_TIMEOUT_MS}`);
      error.code = 'ROOT_SOCKET_READY_TIMEOUT';
      finish(false, error);
    }, ROOT_SOCKET_READY_TIMEOUT_MS);

    try {
      rootSocket.connect();
    } catch (error) {
      finish(false, error);
    }
  });
  return ensureRootPromise;
}

/* =========================================================
 * 컨텍스트 갱신 (로비 식별 갱신용)
 * =======================================================*/
export function updateRootContext({ user_code, table_code, nickname: nn, token: tk } = {}) {
  // ✅ prevUserCode는 localStorage/session fallback을 절대 섞지 않는다.
  // rootSocket이 먼저 연결되고 user_code가 뒤늦게 세팅되는 경우,
  // localStorage fallback 때문에 "이미 같은 유저"로 오판하면 joinLobby 보정이 누락된다.
  const prevUserCode = String(userCode || '').trim();
  const resolvedIdentity = _resolveSessionIdentity();
  const nextUserCode = String(
    typeof user_code !== 'undefined'
      ? user_code
      : (resolvedIdentity.userCode || userCode || '')
  ).trim();

  if (typeof user_code !== "undefined") {
    userCode = user_code;
  } else if (!prevUserCode && nextUserCode) {
    // ✅ payload에 user_code가 없어도 fallback으로 유저가 확인되면 메모리 컨텍스트도 보정한다.
    // 그래야 이후 같은 user_code updateRootContext 반복 호출에서 joinLobby가 다시 나가지 않는다.
    userCode = nextUserCode;
  }
  if (typeof table_code !== "undefined") tableCode = table_code;
  if (typeof nn !== "undefined")         nickname = nn;
  if (typeof tk !== "undefined") { token = tk; if (String(tk || "").trim()) __authCleared = false; }

  rootSocket.auth = { ...(rootSocket.auth || {}), token, user_code: userCode };
  rootSocket.io.opts.query = { userCode, tableCode, nickname };

  if (rootSocket.connected) {
    const userCodeChanged = !!nextUserCode && String(prevUserCode || '') !== String(nextUserCode);

    if (userCodeChanged) {
      // rootSocket이 먼저 연결되고 user_code가 뒤늦게 확정된 케이스 보정.
      // 같은 user_code 반복 update에서는 재전송하지 않는다.
      __rootJoinLobbySent = false;
      __onlineUsersJoinLobbyOnce = false;
      _rootIdentifySent = false;
      _lastIdentifySig = `${userCode}|${tableCode}|${nickname}|${token || ""}`;
      _emitRootIdentifyOnce({ force: true, source: 'updateRootContext:userCodeChanged' });
      _emitJoinLobbyOnce({ silent: true, source: 'updateRootContext:userCodeChanged' });
    } else {
      // [PATCH][ANCHOR:SOCKET:UPDATE_ROOTCONTEXT_FORCE_IDENTIFY_GUARDED]
      // ✅ 값이 실제로 바뀐 경우에만 force identify (연타 방지)
      const sig = `${userCode}|${tableCode}|${nickname}|${token || ""}`;
      if (sig !== _lastIdentifySig) {
        _lastIdentifySig = sig;
        _emitRootIdentifyOnce({ force: true, source: 'updateRootContext' });
        // [ADD][ANCHOR:ROOTSOCKET:UPDATE_ROOTCONTEXT_PRESENCE_PING_ON_CHANGE]
        try {
          if (userCode) rootSocket.emit('presencePing', { user_code: String(userCode) }, () => {});
        } catch (_) {}
      }
    }
  }
  // [ADD][ANCHOR:SOCKET:UPDATE_ROOTCONTEXT_SYNC_CHATSOCKET]
  // ✅ chatSocket도 동일한 identity/token으로 유지
  try {
    _syncChatSocketContext();
  } catch (_) {}

  // ✅ cashierSocket도 동일한 identity/token으로 유지
  try {
    _syncCashierSocketContext?.();
  } catch (_) {}
 }


/* =========================================================
 * recoveryTables 구독/소비 API
 * =======================================================*/
// [FIX][ANCHOR:SOCKET:ON_RECOVERY_TABLES_SOCKET]
export function onRecoveryTablesSocket(cb) {
  const handler = (payload) => { rootSocketDebug('[recoveryTables@root] ⬅', payload); cb?.(payload); };
  rootSocket.on(RECOVERY_TABLES_EVENT, handler);
  return () => rootSocket.off(RECOVERY_TABLES_EVENT, handler);
}

// [ADD][ANCHOR:ROOTSOCKET:TAKE_RECOVERY_TABLES_ONCE]
export function takeRecoveryTablesOnce() {
  let v = _lastRecoveryTables;
  if (!v) {
    try {
      v = JSON.parse(sessionStorage.getItem(RECOVERY_TABLES_STORAGE_KEY) || "null");
    } catch (_) {
      v = null;
    }
  }

  // 1회 소비 후 제거(중복 오픈 방지)
  _lastRecoveryTables = null;
  try { sessionStorage.removeItem(RECOVERY_TABLES_STORAGE_KEY); } catch (_) {}

  // 이전 코드가 남긴 receivedAt 없는 캐시나 오래된 로비 세션 캐시는 복구 근거로 사용하지 않는다.
  // 실제 복구 대상이면 rootSocket이 현재 세션에서 새 recoveryTables를 다시 보내므로 이벤트 구독으로 열린다.
  const receivedAt = Number(v?.receivedAt || v?.received_at || 0);
  if (!receivedAt || Date.now() - receivedAt > RECOVERY_TABLES_CACHE_MAX_AGE_MS) return null;

  // 다른 계정 또는 사용자 식별자가 없는 구형 캐시는 절대 자동 복구 근거로 사용하지 않는다.
  let currentUserCode = '';
  try {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}') || {};
    currentUserCode = String(
      currentUser?.user_code ?? currentUser?.User_code ?? currentUser?.userCode ?? ''
    ).trim();
  } catch (_) {}
  const payloadUserCode = String(v?.userCode ?? v?.user_code ?? '').trim();
  if (currentUserCode && (!payloadUserCode || payloadUserCode !== currentUserCode)) return null;

  return v;
}


function _collectRecoveryCandidateTableCodes() {
  const values = [];
  try {
    for (const key of ['currentTableCode', 'lastTableCode', 'tableCode']) {
      values.push(localStorage.getItem(key));
    }
  } catch (_) {}

  // 사용자가 Root/테이블 창의 X로 닫은 테이블도 서버 검증 후보에 포함한다.
  try {
    const closed = JSON.parse(localStorage.getItem('npc:closedTableRecoveries:v1') || '{}');
    for (const tc of Object.keys(closed && typeof closed === 'object' ? closed : {})) values.push(tc);
  } catch (_) {}

  // 데스크톱 멀티테이블 저장값은 복구 확정값이 아니라 서버 검증 후보로만 전달한다.
  try {
    const parsed = JSON.parse(localStorage.getItem('desktop_multitable_v1') || 'null');
    for (const screen of (Array.isArray(parsed?.screens) ? parsed.screens : [])) {
      const code = String(
        screen?.tableId ||
        (String(screen?.id || '').startsWith('table:') ? String(screen.id).slice('table:'.length) : '')
      ).trim();
      if (code) values.push(code);
    }
  } catch (_) {}

  return Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean)));
}

// [FIX][ANCHOR:ROOTSOCKET:REQUEST_RECOVERY_TABLES]
// 창 종료 직후 게임서버의 is_connected=false 반영이 늦더라도,
// 로컬 테이블 힌트를 서버 SeatDB+Redis+tombstone 검증 후보로 보내 정상 복구를 보완한다.
// 로컬 값만으로는 절대 테이블을 열지 않는다.
export async function requestRecoveryTables({ includeLocalHints = true, timeoutMs = 3000 } = {}) {
  await ensureRootNSReady();

  const identity = _resolveSessionIdentity();
  const candidates = includeLocalHints ? _collectRecoveryCandidateTableCodes() : [];
  const payload = {
    user_code: identity.userCode,
    userCode: identity.userCode,
    candidates,
    tableCodes: candidates,
    currentTableCode: candidates[0] || '',
    ts: Date.now(),
  };

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || { success: false, error: 'empty_recovery_response', tables: [] });
    };
    const timer = setTimeout(() => finish({ success: false, timeout: true, tables: [] }), Math.max(1000, Number(timeoutMs || 3000)));
    try {
      rootSocket.emit('requestRecoveryTables', payload, (response = {}) => {
        clearTimeout(timer);
        finish(response);
      });
    } catch (e) {
      clearTimeout(timer);
      finish({ success: false, error: e?.message || 'request_recovery_tables_failed', tables: [] });
    }
  });
}

// 로그아웃/세션 종료 시 이전 계정의 복구 캐시와 테이블 힌트를 제거한다.
// 정상적인 창 강제 종료 복구는 서버 recoveryTables가 다시 내려오므로 영향을 받지 않는다.
export function clearRecoverySessionCache({ clearTableHints = false } = {}) {
  _lastRecoveryTables = null;
  try { sessionStorage.removeItem(RECOVERY_TABLES_STORAGE_KEY); } catch (_) {}
  try { sessionStorage.removeItem(RECOVERY_OPENED_SS_KEY); } catch (_) {}
  try { sessionStorage.removeItem(RECOVERY_LAST_TS_KEY); } catch (_) {}
  try { localStorage.removeItem(RECOVERY_TABLES_LS_KEY); } catch (_) {}
  try { localStorage.removeItem(RECOVERY_CACHE_KEY); } catch (_) {}

  if (clearTableHints) {
    try {
      for (const key of ['currentTableCode', 'lastTableCode', 'tableCode', 'desktop_multitable_v1']) {
        localStorage.removeItem(key);
      }
    } catch (_) {}
  }
}

// [FIX][ANCHOR:ROOTSOCKET:FORGET_EXPLICITLY_LEFT_TABLE]
// 명시적 leaveTable 성공 직후 로컬의 모든 복구 캐시에서 해당 테이블을 제거한다.
// 단순 창 닫기/네트워크 끊김에서는 호출하지 않으므로 정상 복구 동작은 그대로 유지된다.
export function forgetRecoveryTable(tableCode) {
  const tc = String(tableCode || '').trim();
  if (!tc) return false;

  const filterPayload = (value) => {
    if (!value || typeof value !== 'object') return value;
    const tables = (Array.isArray(value?.tables) ? value.tables : [])
      .filter((t) => String(t?.tableCode ?? t?.table_code ?? '').trim() !== tc);
    return { ...value, tables };
  };

  let changed = false;
  try {
    const next = filterPayload(_lastRecoveryTables);
    if (next !== _lastRecoveryTables) { _lastRecoveryTables = next; changed = true; }
  } catch (_) {}

  const rewriteJsonStorage = (storage, key) => {
    try {
      const raw = storage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const next = filterPayload(parsed);
      if (Array.isArray(next?.tables) && next.tables.length > 0) storage.setItem(key, JSON.stringify(next));
      else storage.removeItem(key);
      changed = true;
    } catch (_) {}
  };

  try { rewriteJsonStorage(sessionStorage, RECOVERY_TABLES_STORAGE_KEY); } catch (_) {}
  try { rewriteJsonStorage(localStorage, RECOVERY_TABLES_LS_KEY); } catch (_) {}
  try { rewriteJsonStorage(localStorage, RECOVERY_CACHE_KEY); } catch (_) {}

  try {
    const opened = _loadOpenedMap();
    if (opened && Object.prototype.hasOwnProperty.call(opened, tc)) {
      delete opened[tc];
      sessionStorage.setItem(RECOVERY_OPENED_SS_KEY, JSON.stringify(opened));
      changed = true;
    }
  } catch (_) {}

  try { localStorage.removeItem(`${JOINED_TABLE_META_LS_PREFIX}${tc}`); } catch (_) {}
  try { sessionStorage.removeItem(`${JOINED_TABLE_ACKED_SS_PREFIX}${tc}`); } catch (_) {}

  // 이미 구독 중인 Root에도 필터링된 최신 목록을 전달한다.
  try {
    const next = filterPayload(_lastRecoveryTables || { ts: Date.now(), tables: [] });
    _broadcastRecoveryTables(next);
  } catch (_) {}

  return changed;
}

// [ADD][ANCHOR:ROOTSOCKET:ONLINE_USERS_SNAPSHOT_API]
// ✅ 온라인 유저 스냅샷 가져오기 (Redis presence 기반)
// - 1순위: 서버 getOnlineUsersSnapshot(ACK) 지원 시 사용
// - 2순위: 서버 getOnlineUsers(ACK) 구형 호환
// - 2순위: 서버 onlineUsersSnapshot 푸시가 오면 사용
// - 3순위: (구형 폴백) joinLobby(silent:true) → initialActiveUsers 1회 수신
// [ADD][ANCHOR:ROOTSOCKET:ONLINE_USERS_SNAPSHOT_JOINLOBBY_ONCE_STATE]
// ✅ 폴백 joinLobby는 "딱 1회만" (패널/폴링이 joinLobby를 연타하면 userJoined 브로드캐스트로 깜빡임 발생)
let __onlineUsersJoinLobbyOnce = false;
export async function getOnlineUsersSnapshot({ timeoutMs = 1200, identity } = {}) {
  await ensureRootNSReady();

  const u = identity || _safeParseUser();
  const myId = u?.id ?? u?.ID;
  const myCode = String(u?.user_code ?? u?.User_code ?? userCode ?? '').trim();

  return new Promise((resolve) => {
    let done = false;
    let t = null;
    let lastList = null;
    const deadline = Date.now() + Number(timeoutMs || 1200);

    const cleanup = () => {
      try { rootSocket.off('initialActiveUsers', onInitial); } catch (_) {}
      try { rootSocket.off('onlineUsersSnapshot', onSnapshot); } catch (_) {}
      try { if (t) clearTimeout(t); } catch (_) {}
    };

    const finish = (list) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(Array.isArray(list) ? list : null);
    };

    const consider = (list) => {
      if (!Array.isArray(list)) return;
      lastList = list;
      // ✅ 비어있어도 바로 확정하지 말고, timeout까지 기다렸다가 확정
      if (list.length > 0) finish(list);
    };
    const onInitial = (payload) => consider(payload?.data || []);
    const onSnapshot = (payload) => consider(payload?.data || payload?.users || []);

    // 1) 혹시 푸시가 먼저 오면 그걸로 처리
    rootSocket.once('initialActiveUsers', onInitial);
    rootSocket.once('onlineUsersSnapshot', onSnapshot);

    // Presence는 rootSocket 전역 heartbeat가 이미 유지한다.
    // 온라인 목록 조회마다 presencePing을 추가로 쏘지 않아 대규모 Redis write를 줄인다.

    // ✅ 2) ACK 기반: 빈 배열이면 timeout 전까지 짧게 재시도
    const tryGet = () => {
      if (done) return;
      try {
        rootSocket.emit('getOnlineUsersSnapshot', { limit: 500 }, (res) => {
          const list = res?.data || res?.users;
          if (Array.isArray(list)) {
            consider(list);
          } else {
            rootSocket.emit('getOnlineUsers', { limit: 500 }, (legacyRes) => {
              const legacyList = legacyRes?.data || legacyRes?.users;
              consider(Array.isArray(legacyList) ? legacyList : []);
            });
          }
          if (done) return;
          if (Date.now() < deadline && (!Array.isArray(list) || list.length === 0)) {
            setTimeout(tryGet, 250);
          }
        });
      } catch (_) {}
    };
    tryGet();

    // 3) 폴백: joinLobby로 스냅샷 요청 (silent 미지원 서버에서도 "1회만" 실행)
    try {
      // [PATCH][ANCHOR:ROOTSOCKET:ONLINE_USERS_SNAPSHOT_JOINLOBBY_ONCE]
      if (!done && !__onlineUsersJoinLobbyOnce && myCode) {
        __onlineUsersJoinLobbyOnce = true;
        rootSocket.emit('joinLobby', {
          ID: myId,
          User_code: myCode,
          user_code: myCode,
          silent: true,
        });
      }
    } catch (_) {}

    // ✅ timeout 시점엔 마지막으로 받은 값(대부분 [] 또는 최신 snapshot)을 확정
    t = setTimeout(() => finish(lastList || []), timeoutMs);
  });
}


// [ADD][ANCHOR:ROOTSOCKET:ONLINE_USERS_SCALABLE_API]
// 대규모 로비용 온라인 유저 프로토콜.
// - 최초/추가 목록은 cursor page(기본 100명)
// - 접속/종료 변화는 onlineUsersDelta 작은 batch
// - 여러 UI가 같은 rootSocket을 공유하므로 구독 해제는 ref-count로 보호한다.
let __onlineUsersDeltaConsumers = 0;

function _emitRootAckWithTimeout(eventName, payload = {}, timeoutMs = 1500) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`${eventName}_timeout`));
    }, Math.max(300, Number(timeoutMs || 1500)));

    try {
      rootSocket.emit(eventName, payload, (res) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (res?.success === false) {
          reject(new Error(String(res?.error || `${eventName}_failed`)));
          return;
        }
        resolve(res || {});
      });
    } catch (e) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(e);
    }
  });
}

function _normalizeOnlineUsersPageResponse(res = {}) {
  const users = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.users) ? res.users : []);
  const nextCursor = String(res?.nextCursor ?? res?.next_cursor ?? '0').trim() || '0';
  const totalRaw = res?.totalOnline ?? res?.total_online;
  const totalOnline = Number.isFinite(Number(totalRaw)) ? Math.max(0, Number(totalRaw)) : null;
  return { users, nextCursor, totalOnline, ts: Number(res?.ts || Date.now()) };
}

export async function acquireOnlineUsersDeltaSubscription({ limit = 100, timeoutMs = 1500 } = {}) {
  await ensureRootNSReady();
  __onlineUsersDeltaConsumers += 1;
  try {
    const res = await _emitRootAckWithTimeout('subscribeOnlineUsersDelta', { cursor: '0', limit }, timeoutMs);
    return _normalizeOnlineUsersPageResponse(res);
  } catch (e) {
    __onlineUsersDeltaConsumers = Math.max(0, __onlineUsersDeltaConsumers - 1);
    throw e;
  }
}

export async function getOnlineUsersPage({ cursor = '0', limit = 100, timeoutMs = 1500 } = {}) {
  await ensureRootNSReady();
  const res = await _emitRootAckWithTimeout('getOnlineUsersPage', { cursor, limit }, timeoutMs);
  return _normalizeOnlineUsersPageResponse(res);
}

// Socket.IO reconnect 후 서버 room만 다시 가입한다. consumer ref-count는 증가시키지 않는다.
export async function resubscribeOnlineUsersDeltaSubscription({ limit = 100, timeoutMs = 1500 } = {}) {
  await ensureRootNSReady();
  const res = await _emitRootAckWithTimeout('subscribeOnlineUsersDelta', { cursor: '0', limit }, timeoutMs);
  return _normalizeOnlineUsersPageResponse(res);
}

export function releaseOnlineUsersDeltaSubscription() {
  __onlineUsersDeltaConsumers = Math.max(0, __onlineUsersDeltaConsumers - 1);
  if (__onlineUsersDeltaConsumers !== 0) return;
  try {
    if (rootSocket?.connected) rootSocket.emit('unsubscribeOnlineUsersDelta', {}, () => {});
  } catch (_) {}
}

export function onOnlineUsersDelta(handler) {
  if (typeof handler !== 'function') return () => {};
  rootSocket.on('onlineUsersDelta', handler);
  return () => {
    try { rootSocket.off('onlineUsersDelta', handler); } catch (_) {}
  };
}

/* =========================================================
 * 로비/매칭 관련 API
 * =======================================================*/
export async function joinQueue({ display_code, user_code, request_id } = {}, callback) {
  const resolvedUserCode = String(user_code ?? userCode ?? '').trim();
  const requestId = String(request_id || createMatchRequestId()).trim();
  const payload = { display_code: String(display_code || '').trim(), user_code: resolvedUserCode, request_id: requestId };
  const startedAt = Date.now();
  const initialWaitMeta = _normalizeMatchWaitMeta({
    ...payload,
    queued_at: startedAt,
    prompt_at: startedAt + MATCH_WAIT_PROMPT_AFTER_MS,
    expires_at: startedAt + MATCH_WAIT_TOTAL_MS,
  });
  savePendingMatchRequest({ ...payload, ...initialWaitMeta, state: 'submitting', started_at: startedAt });
  _scheduleMatchWaitPrompt(initialWaitMeta);
  rootSocketDebug('[joinQueue] ▶ emit(root)', payload);

  // 새 테이블/같은 그룹 추가 입장 직전 Access Token을 선제 갱신한다.
  // 기존 게임 소켓이 살아 있어도 새 Socket/HTTP 연결에서 TOKEN_EXPIRED가 터지는 문제를 막는다.
  try {
    const freshToken = await ensureFreshAccessToken();
    if (freshToken) {
      token = freshToken;
      rootSocket.auth = { ...(rootSocket.auth || {}), token: freshToken, user_code: resolvedUserCode || userCode };
    }
  } catch (refreshError) {
    if (isDefinitiveAuthFailure(refreshError)) {
      clearPendingMatchRequest(requestId, resolvedUserCode);
      closeMatchWaitUi(requestId);
      clearLocalAuthAndGoLogin(refreshError?.code || 'session');
      return { success: false, error: refreshError?.code || 'auth_refresh_failed', request_id: requestId };
    }
    // Redis/네트워크 일시 장애면 기존 연결을 유지한 채 매칭 요청 자체는 계속 시도한다.
  }

  await ensureRootNSReady();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (res = {}) => {
      if (settled) return;
      settled = true;
      const normalized = { request_id: requestId, ...res };
      if (normalized?.success) {
        // joinedTable can be delivered and opened before the joinQueue ACK. Do
        // not recreate the pending request or reopen the wait UI after delivery.
        if (_hasAckedJoinedTable(requestId)) {
          clearPendingMatchRequest(requestId, resolvedUserCode);
          closeMatchWaitUi(requestId);
        } else {
          const statusMeta = normalized?.status && typeof normalized.status === 'object' ? normalized.status : {};
          const waitMeta = _normalizeMatchWaitMeta({ ...payload, ...statusMeta, ...normalized });
          savePendingMatchRequest({ ...payload, ...statusMeta, ...normalized, ...waitMeta, state: statusMeta?.state || 'queued' });
          const state = String(statusMeta?.state || '').toLowerCase();
          if (['matched', 'failed', 'cancelled', 'expired'].includes(state)) closeMatchWaitUi(requestId);
          else _scheduleMatchWaitPrompt(waitMeta);
        }
      } else if (!normalized?.recoverable) {
        clearPendingMatchRequest(requestId, resolvedUserCode);
        closeMatchWaitUi(requestId);
      }
      callback?.(normalized);
      resolve(normalized);
    };

    const timer = setTimeout(async () => {
      const statusResult = await getMatchRequestStatus(requestId, { timeoutMs: Math.min(MATCH_STATUS_TIMEOUT_MS, 3000) }).catch(() => null);
      if (statusResult?.success && statusResult?.status) {
        return finish({ success: true, ok: true, recovered: true, info: 'status_after_join_ack_timeout', status: statusResult.status });
      }
      finish({ success: false, error: 'join_queue_ack_timeout', recoverable: true });
    }, JOIN_QUEUE_ACK_TIMEOUT_MS);

    try {
      rootSocket.emit('joinQueue', payload, (res) => {
        clearTimeout(timer);
        rootSocketDebug('[joinQueue] ⬅ ack', res);
        finish(res || { success: false, error: 'empty_join_queue_ack' });
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: error?.message || 'join_queue_emit_failed', recoverable: true });
    }
  });
}

export async function confirmMatchQueueWait({ request_id, user_code } = {}, options = {}) {
  const requestId = String(request_id || __matchWaitUiState.request_id || '').trim();
  if (!requestId) return { success: false, error: 'request_id_required' };
  await ensureRootNSReady();
  const timeoutMs = Math.max(1000, Number(options?.timeoutMs || MATCH_STATUS_TIMEOUT_MS));
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      const responseState = String(value?.status?.state || '').toLowerCase();
      if (['failed', 'cancelled', 'expired'].includes(responseState)) {
        clearPendingMatchRequest(requestId, user_code);
        closeMatchWaitUi(requestId);
      } else if (['matched', 'matched_delivery_pending'].includes(responseState)) {
        const current = getPendingMatchRequest(user_code);
        savePendingMatchRequest({
          ...(current || {}),
          ...(value?.status || {}),
          request_id: requestId,
          user_code: String(user_code || __matchWaitUiState.user_code || _currentJoinedTableUserCode() || '').trim(),
          state: responseState,
        });
      } else if (value?.success) {
        const current = getPendingMatchRequest(user_code);
        savePendingMatchRequest({
          ...(current || {}),
          request_id: requestId,
          user_code: String(user_code || __matchWaitUiState.user_code || _currentJoinedTableUserCode() || '').trim(),
          long_wait_confirmed: true,
          long_wait_confirmed_at: Date.now(),
        });
        _emitMatchWaitUiState({ open: true, phase: 'waiting', long_wait_confirmed: true });
      }
      resolve(value || { success: false, error: 'empty_confirm_wait_ack' });
    };
    const timer = setTimeout(() => finish({ success: false, error: 'confirm_wait_timeout', request_id: requestId }), timeoutMs);
    try {
      rootSocket.emit('confirmMatchQueueWait', {
        request_id: requestId,
        user_code: String(user_code || __matchWaitUiState.user_code || _currentJoinedTableUserCode() || '').trim(),
      }, (res) => {
        clearTimeout(timer);
        finish(res);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: error?.message || 'confirm_wait_emit_failed', request_id: requestId });
    }
  });
}

export async function cancelMatchQueue({ request_id, user_code, reason = 'user_cancelled_long_wait' } = {}, options = {}) {
  const requestId = String(request_id || __matchWaitUiState.request_id || '').trim();
  if (!requestId) return { success: false, error: 'request_id_required' };
  await ensureRootNSReady();
  const timeoutMs = Math.max(1000, Number(options?.timeoutMs || MATCH_STATUS_TIMEOUT_MS));
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      const responseState = String(value?.status?.state || '').toLowerCase();
      if (['matched', 'matched_delivery_pending'].includes(responseState)) {
        const current = getPendingMatchRequest(user_code);
        savePendingMatchRequest({
          ...(current || {}),
          ...(value?.status || {}),
          request_id: requestId,
          user_code: String(user_code || __matchWaitUiState.user_code || _currentJoinedTableUserCode() || '').trim(),
          state: responseState,
        });
      } else if (value?.cancelled || ['failed', 'cancelled', 'expired'].includes(responseState)) {
        clearPendingMatchRequest(requestId, user_code);
        closeMatchWaitUi(requestId);
      } else if (value?.success) {
        clearPendingMatchRequest(requestId, user_code);
        closeMatchWaitUi(requestId);
      }
      resolve(value || { success: false, error: 'empty_cancel_match_ack' });
    };
    const timer = setTimeout(() => finish({ success: false, error: 'cancel_match_timeout', request_id: requestId }), timeoutMs);
    try {
      rootSocket.emit('cancelMatchQueue', {
        request_id: requestId,
        user_code: String(user_code || __matchWaitUiState.user_code || _currentJoinedTableUserCode() || '').trim(),
        reason: String(reason || 'user_cancelled_long_wait'),
      }, (res) => {
        clearTimeout(timer);
        finish(res);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: error?.message || 'cancel_match_emit_failed', request_id: requestId });
    }
  });
}

export async function getMatchRequestStatus(requestId = '', options = {}) {
  const request_id = String(requestId || '').trim();
  if (!request_id) return { success: false, error: 'request_id_required' };
  await ensureRootNSReady();
  const timeoutMs = Math.max(1000, Number(options?.timeoutMs || MATCH_STATUS_TIMEOUT_MS));
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || { success: false, error: 'empty_match_status_ack', request_id });
    };
    const timer = setTimeout(() => finish({ success: false, error: 'match_status_timeout', request_id }), timeoutMs);
    try {
      rootSocket.emit('getMatchRequestStatus', { request_id, user_code: _currentJoinedTableUserCode() }, (res) => {
        clearTimeout(timer);
        finish(res);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: error?.message || 'match_status_emit_failed', request_id });
    }
  });
}

export async function ackMatchRequestDelivery({ request_id, table_code, user_code } = {}, options = {}) {
  const requestId = String(request_id || '').trim();
  const tableCode = String(table_code || '').trim();
  if (!requestId || !tableCode) return { success: false, error: 'invalid_delivery_ack' };
  await ensureRootNSReady();
  const timeoutMs = Math.max(1000, Number(options?.timeoutMs || MATCH_STATUS_TIMEOUT_MS));
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || { success: false, error: 'empty_delivery_ack' });
    };
    const timer = setTimeout(() => finish({ success: false, error: 'delivery_ack_timeout', request_id: requestId }), timeoutMs);
    try {
      rootSocket.emit('ackMatchRequestDelivery', {
        request_id: requestId,
        table_code: tableCode,
        user_code: String(user_code || _currentJoinedTableUserCode() || '').trim(),
      }, (res) => {
        clearTimeout(timer);
        finish(res);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: error?.message || 'delivery_ack_emit_failed', request_id: requestId });
    }
  });
}

export function onRootSocketConnect(cb) {
  const handler = () => cb?.({ socket_id: rootSocket.id, ts: Date.now() });
  rootSocket.on('connect', handler);
  if (rootSocket.connected) setTimeout(handler, 0);
  return () => rootSocket.off('connect', handler);
}

export function onQueueUpdated(cb) {
  const handler = (payload) => { rootSocketDebug("[queueUpdated] ⬅", payload); cb?.(payload); };
  rootSocket.on("queueUpdated", handler);
  return () => rootSocket.off("queueUpdated", handler);
}

// [ADD][ANCHOR:ROOTSOCKET:JOINED_TABLE_META_CACHE_BIND]
function _handleJoinedTableMetaCache(data = {}, ack) {
  const validation = _validateJoinedTablePayload(data);
  if (!validation.ok) {
    rootSocketWarn("[joinedTable@root:cache] rejected", validation);
    _ackJoinedTable(ack, validation);
    return;
  }

  const norm = _cacheJoinedTableMeta(data);
  // Cache only. The request remains pending until a screen confirms that the
  // table window/view was actually opened successfully.
  rootSocketDebug("[joinedTable@root:cache] ⬅", norm);
  // ACK는 실제 joinedTable 소비자(onJoinedTable)가 popup/open 시도를 시작한 뒤 보낸다.
}
try { rootSocket.off("joinedTable", _handleJoinedTableMetaCache); } catch (_) {}
rootSocket.on("joinedTable", _handleJoinedTableMetaCache);


export function onJoinedTable(cb) {
  // 서버가 / 루트에서 joinedTable을 쏘는 경우. 실제 테이블 열기 시도가 성공한 뒤 ACK한다.
  const handler = async (data = {}, ack) => {
    const validation = _validateJoinedTablePayload(data);
    if (!validation.ok) {
      rootSocketWarn('[joinedTable@root] rejected', validation);
      _ackJoinedTable(ack, validation);
      return;
    }

    const requestId = validation.request_id;
    if (requestId && _hasAckedJoinedTable(requestId)) {
      const norm = _cacheJoinedTableMeta(data);
      rootSocketDebug('[joinedTable@root] duplicate ack', norm);
      _ackJoinedTable(ack, { ok: true, request_id: requestId, duplicate: true });
      return;
    }

    const norm = _cacheJoinedTableMeta(data);
    rootSocketDebug('[joinedTable@root] ⬅', norm);
    try {
      const result = await Promise.resolve(cb?.(norm));
      const accepted = result === true || result?.ok === true || result?.success === true;
      if (!accepted) {
        _ackJoinedTable(ack, { ok: false, request_id: requestId, reason: result?.reason || 'table_open_rejected' });
        return;
      }
      if (requestId) {
        _markAckedJoinedTable(requestId);
        clearPendingMatchRequest(requestId, data?.user_code || data?.userCode);
        closeMatchWaitUi(requestId);
      }
      _ackJoinedTable(ack, {
        ok: true,
        received: true,
        opened: true,
        request_id: requestId,
        table_code: norm.table_code || norm.tableCode,
        acked_at: Date.now(),
      });
    } catch (error) {
      rootSocketWarn('[joinedTable@root] client handler error before ack', { request_id: requestId, message: error?.message });
      _ackJoinedTable(ack, { ok: false, request_id: requestId, reason: error?.message || 'table_open_failed' });
    }
  };
  rootSocket.on('joinedTable', handler);
  return () => rootSocket.off('joinedTable', handler);
}

export function onMatchDispatching(cb) {
  const handler = (payload = {}) => {
    rootSocketDebug("[matchDispatching@root] ⬅", payload);
    cb?.(payload);
  };
  rootSocket.on("matchDispatching", handler);
  return () => rootSocket.off("matchDispatching", handler);
}

export function onMatchFailed(cb) {
  const events = ["matchFailed", "joinFailed", "create_failed", "admit_failed", "duplicate_live_table"];
  const handler = (eventName) => (payload = {}) => {
    const normalized = {
      ok: false,
      success: false,
      retryable: payload?.retryable !== false,
      ...payload,
      event: payload?.event || eventName,
      error: payload?.error || payload?.reason || eventName,
      reason: payload?.reason || payload?.error || eventName,
    };
    rootSocketWarn(`[${eventName}@root] ⬅`, normalized);
    closeMatchWaitUi(normalized?.request_id);
    clearPendingMatchRequest(normalized?.request_id, normalized?.user_code);
    cb?.(normalized);
  };
  const handlers = events.map((eventName) => [eventName, handler(eventName)]);
  handlers.forEach(([eventName, fn]) => rootSocket.on(eventName, fn));
  return () => handlers.forEach(([eventName, fn]) => rootSocket.off(eventName, fn));
}

export function onQueueExpired(cb) {
  const handler = (payload = {}) => {
    rootSocketWarn("[queueExpired@root] ⬅", payload);
    closeMatchWaitUi(payload?.request_id);
    clearPendingMatchRequest(payload?.request_id, payload?.user_code);
    cb?.(payload);
  };
  rootSocket.on("queueExpired", handler);
  return () => rootSocket.off("queueExpired", handler);
}



/* =========================================================
 * display_set realtime (ROOM: disp:{display_code})
 * =======================================================*/
export async function subscribeDisplaySets(display_codes = []) {
  const codes = Array.from(new Set(
    (Array.isArray(display_codes) ? display_codes : [])
      .map((code) => String(code || '').trim())
      .filter(Boolean)
  ));
  if (!codes.length) return [];
  codes.forEach((code) => __displaySetSubscriptionCodes.add(code));
  await ensureRootNSReady();
  rootSocket.emit("subscribeDisplaySets", { display_codes: codes });
  rootSocketDebug("[subscribeDisplaySets] ▶", codes);
  return codes;
}

export function onDisplaySetUpdated(cb) {
  const handler = (payload) => {
    rootSocketDebug("[DISPLAY_SET_UPDATED] ⬅", payload);
    cb?.(payload);
  };
  rootSocket.on("DISPLAY_SET_UPDATED", handler);
  return () => rootSocket.off("DISPLAY_SET_UPDATED", handler);
}

/* =========================================================
 * recoveryTables → 자동 오픈(기본 ON)
 * - 백엔드 joinLobby 직후, 해당 유저 소켓으로만 recoveryTables가 날아옴
 * - 여기서 즉시 저장 + 커스텀 이벤트 발생 + 테이블 오픈 시도
 * =======================================================*/
// [ADD][ANCHOR:SOCKET:RECOVERY_TABLES_AUTO_BIND]
let _recoveryAutoBound = false;
export function enableAutoOpenRecoveryTablesOnce() {
  if (_recoveryAutoBound) return () => {};
  _recoveryAutoBound = true;

  const AUTO_OPEN =
    String(process.env.REACT_APP_AUTO_OPEN_RECOVERY_TABLES || 'true').toLowerCase() !== 'false';

  const handler = (payload = {}) => {
    const norm = _normalizeRecoveryTables(payload);
    const ts = Number(norm?.ts || 0) || Date.now();
    const tables = _dedupeRecoveryTables(norm?.tables || []);
    if (!tables.length) return;

    const lastTs = Number(sessionStorage.getItem(RECOVERY_LAST_TS_KEY) || 0);
    if (lastTs && ts <= lastTs) {
      rootSocketDebug('[recoveryTables] skip duplicated', { ts, lastTs });
      return;
    }
    try { sessionStorage.setItem(RECOVERY_LAST_TS_KEY, String(ts)); } catch {}
     // ✅ takeRecoveryTablesOnce()가 읽는 키로 캐시
    _cacheRecoveryTables({ ts, tables });
    // ✅ 앱 전체에서 구독 가능한 CustomEvent 브로드캐스트
    _broadcastRecoveryTables({ ts, tables });

     // ✅ 이미 테이블/팝업 화면이면 중복 오픈 방지
    const path = String(window.location?.pathname || '');
    const hasQueryTable = !!new URLSearchParams(window.location.search).get('tableCode');
    const inTablePage = hasQueryTable || path.startsWith('/table') || path.startsWith('/popup');
    if (!AUTO_OPEN || inTablePage) return;

    // ✅ socket.js는 복구 테이블을 직접 열지 않는다.
    // 실제 오픈/화면전환은 라우터 또는 MobileMultiTableRoot에서 처리한다.
  };

  rootSocket.on(RECOVERY_TABLES_EVENT, handler);
  return () => rootSocket.off(RECOVERY_TABLES_EVENT, handler);
}

// [FIX][ANCHOR:SOCKET:RECOVERY_TABLES_AUTO_BIND_DISABLE_DEFAULT]
// ✅ 중복 처리/중복 오픈 방지:
// - recoveryTables 처리는 RECOVERY_TABLES_EVENT → _onRecoveryTables 단일 루트로 통일
// - 필요 시, 앱 진입점(예: App.jsx)에서 명시적으로 enableAutoOpenRecoveryTablesOnce() 호출
// enableAutoOpenRecoveryTablesOnce();


/* =========================================================
 * cashier / payment helpers
 * =======================================================*/
const CASHIER_RESULT_EVENT = 'npc:cashier:result:update';
const CASHIER_STATUS_EVENT = 'npc:cashier:status:update';
const CASHIER_HISTORY_REQUEST_EVENT = (EVENTS && EVENTS.CASHIER_HISTORY_REQUEST) || 'cashier:history:request';
const CASHIER_HISTORY_DELETE_REQUEST_EVENT = (EVENTS && EVENTS.CASHIER_HISTORY_DELETE_REQUEST) || 'cashier:history:delete';
const CASHIER_CANCEL_REQUEST_EVENT = (EVENTS && EVENTS.CASHIER_CANCEL_REQUEST) || 'cashier:request:cancel';
const CASHIER_RECHECK_REQUEST_EVENT = (EVENTS && EVENTS.CASHIER_RECHECK_REQUEST) || 'cashier:request:recheck';
const CASHIER_DEPOSIT_SESSION_EXTEND_EVENT = (EVENTS && EVENTS.CASHIER_DEPOSIT_SESSION_EXTEND) || 'cashier:deposit:session:extend';
const CASHIER_DEPOSIT_FINALIZE_PARTIAL_EVENT = (EVENTS && EVENTS.CASHIER_DEPOSIT_FINALIZE_PARTIAL) || 'cashier:deposit:finalize-partial';
const CASHIER_DEPOSIT_REFUND_REQUEST_EVENT = (EVENTS && EVENTS.CASHIER_DEPOSIT_REFUND_REQUEST) || 'cashier:deposit:refund-request';
const PAYMENT_ADDRESS_LIST_EVENT = 'paymentAddress:get';
const PAYMENT_ADDRESS_SAVE_EVENT = 'paymentAddress:save';
let __cashierSocketBound = false;
let ensureCashierPromise = null;

function _dispatchCashierCustomEvent(name, detail) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch (_) {}
}

function _safeUpper(v = '') {
  return String(v ?? '').trim().toUpperCase();
}

function _safeLower(v = '') {
  return String(v ?? '').trim().toLowerCase();
}

function _expandExponentDecimal(input = '') {
  const raw = String(input ?? '').trim();
  if (!/[eE]/.test(raw)) return raw;

  const sign = raw.startsWith('-') ? '-' : '';
  const unsigned = raw.replace(/^[+-]/, '');
  const match = unsigned.match(/^(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
  if (!match) return raw;

  const intPart = match[1] || '0';
  const fracPart = match[2] || '';
  const exponent = Number(match[3] || 0);
  if (!Number.isFinite(exponent)) return raw;

  const digits = `${intPart}${fracPart}`.replace(/^0+(?=\d)/, '') || '0';
  const decimalIndex = intPart.length;
  const shiftedIndex = decimalIndex + exponent;

  if (shiftedIndex <= 0) {
    return `${sign}0.${'0'.repeat(Math.abs(shiftedIndex))}${digits}`;
  }

  if (shiftedIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(shiftedIndex - digits.length)}`;
  }

  return `${sign}${digits.slice(0, shiftedIndex)}.${digits.slice(shiftedIndex)}`;
}

function _normalizeCashierAmount(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return _expandExponentDecimal(raw);
}

function _simpleHash(input = '') {
  let h = 2166136261;
  const src = String(input || '');
  for (let i = 0; i < src.length; i += 1) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

function _normalizeSocketBase(url = '') {
  const v = String(url || '').trim().replace(/\/+$/, '');
  if (!v) return '';
  if (v.endsWith('/cashier')) return v.slice(0, -8);
  return v;
}

function _resolveCashierSocketBase() {
  const env = String(
    process.env.REACT_APP_CASHIER_SOCKET_URL ||
    process.env.REACT_APP_CASHIER_SERVER_URL ||
    process.env.REACT_APP_CASHIER_URL ||
    process.env.REACT_APP_CASHIER_BASE_URL ||
    ''
  ).trim();
  if (env) return _normalizeSocketBase(env);

  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:3200`;
}

const CASHIER_NAMESPACE = String(process.env.REACT_APP_CASHIER_NAMESPACE || '/cashier').trim() || '/cashier';
const CASHIER_SOCKET_BASE = _resolveCashierSocketBase();
const CASHIER_SOCKET_URL = _normalizeNsUrl(CASHIER_SOCKET_BASE, CASHIER_NAMESPACE);

function _buildCashierSocketAuth() {
  const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
  return {
    token: getStoredAccessToken() || token,
    user_code: String(uc || '').trim(),
    nickname: String(nn || '').trim(),
  };
}

function _buildCashierSocketQuery() {
  const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
  return {
    userCode: String(uc || '').trim(),
    nickname: String(nn || '').trim(),
  };
}

export const cashierSocket = io(CASHIER_SOCKET_URL, {
  ...IO_COMMON,
  autoConnect: false,
  auth: _buildCashierSocketAuth(),
  query: _buildCashierSocketQuery(),
});

function _syncCashierSocketContext() {
  cashierSocket.auth = _buildCashierSocketAuth();
  cashierSocket.io.opts.query = _buildCashierSocketQuery();
}

function _normalizeCashierRequestPayload(payload = {}, requestType = '') {
  const { userCode: uc, nickname: nn } = _resolveSessionIdentity();
  const resolvedType = _safeLower(payload?.request_type || requestType);
  const isDepositRequest = resolvedType === 'deposit';
  const depositFromAddress = String(
    payload?.deposit_from_address ??
    payload?.depositFromAddress ??
    payload?.from_address ??
    payload?.fromAddress ??
    payload?.sender_address ??
    payload?.senderAddress ??
    payload?.address ??
    ''
  ).trim();
  const address = isDepositRequest
    ? depositFromAddress
    : String(payload?.address ?? payload?.wallet_address ?? payload?.to_address ?? payload?.from_address ?? '').trim();
  const rawNetworkField = String(payload?.network || '').trim();
  const rawNetworkCode = payload?.network_code
    ?? payload?.chain_code
    ?? (['mainnet', 'testnet'].includes(_safeLower(rawNetworkField)) ? '' : rawNetworkField)
    ?? 'TRC20';
  const rawNetworkName = payload?.network_name
    ?? payload?.networkName
    ?? payload?.network_env
    ?? (['mainnet', 'testnet'].includes(_safeLower(rawNetworkField)) ? rawNetworkField : '')
    ?? 'testnet';
  const networkCode = _safeUpper(rawNetworkCode || 'TRC20');
  const networkName = _safeLower(rawNetworkName || 'testnet') === 'testnet' ? 'testnet' : 'mainnet';
  const assetCode = _safeUpper(payload?.asset_code || payload?.asset || 'USDT');

  return {
    ...payload,
    request_type: resolvedType,
    user_code: String(payload?.user_code || uc || '').trim(),
    nickname: String(payload?.nickname || nn || '').trim(),
    address,
    deposit_from_address: isDepositRequest ? depositFromAddress : String(payload?.deposit_from_address || payload?.depositFromAddress || '').trim(),
    // deposit에서는 wallet_address를 절대 address로 보정하지 않는다. 이 값은 서버가 배정한 입금지갑 주소 전용이다.
    // withdraw에서도 address가 유저 목적지 주소이므로 wallet_address로 복제하지 않는다.
    // 서버가 wallet_address를 bridge wallet 후보로 해석하면 출금 브릿지 지갑 주소와 충돌한다.
    wallet_address: isDepositRequest ? String(payload?.wallet_address || '').trim() : String(payload?.wallet_address || '').trim(),
    network_code: networkCode,
    network_name: networkName,
    network: networkName,
    asset_code: assetCode,
    amount: _normalizeCashierAmount(payload?.amount),
  };
}

function _normalizeCashierAck(ack = {}, fallbackType = '') {
  const payload = ack && typeof ack === 'object' ? ack : {};
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const count = Number(payload?.count);
  const limit = Number(payload?.limit);
  const bridgeDispatchDelivered = payload?.bridge_dispatch_delivered === true;
  const bridgeSignalDelivered = payload?.bridge_signal_delivered === true || bridgeDispatchDelivered;
  const bridgeSignalAcknowledged = payload?.bridge_signal_acknowledged === true || bridgeDispatchDelivered;

  return {
    ok: Boolean(payload?.ok),
    duplicated: Boolean(payload?.duplicated),
    code: String(payload?.code || payload?.error_code || '').trim(),
    error_code: String(payload?.error_code || payload?.code || '').trim(),
    message: String(payload?.message || payload?.error || '').trim(),
    request_id: String(payload?.request_id || '').trim(),
    bridge_id: String(payload?.bridge_id || '').trim(),
    request_type: _safeLower(payload?.request_type || fallbackType),
    status: _safeLower(payload?.status),
    step: _safeUpper(payload?.step),
    deposit_wallet_address: String(payload?.deposit_wallet_address || '').trim(),
    bridge_wallet_address: String(payload?.bridge_wallet_address || '').trim(),
    wallet_address: String(payload?.wallet_address || '').trim(),
    network_code: _safeUpper(payload?.network_code),
    network_name: _safeLower(payload?.network_name || payload?.networkName || payload?.network),
    asset_code: _safeUpper(payload?.asset_code),
    amount: _normalizeCashierAmount(payload?.amount),
    bridge_dispatch_delivered: bridgeDispatchDelivered,
    bridge_dispatch_code: String(payload?.bridge_dispatch_code || '').trim(),
    bridge_dispatch_message: String(payload?.bridge_dispatch_message || '').trim(),
    bridge_signal_delivered: bridgeSignalDelivered,
    bridge_signal_acknowledged: bridgeSignalAcknowledged,
    bridge_signal_code: String(payload?.bridge_signal_code || payload?.bridge_dispatch_code || '').trim(),
    bridge_signal_message: String(payload?.bridge_signal_message || payload?.bridge_dispatch_message || '').trim(),
    request_display: _safeUpper(payload?.request_display),
    display_action: _safeUpper(payload?.display_action),
    manual_review_required: payload?.manual_review_required === true,
    wallet_queue_waiting: payload?.wallet_queue_waiting === true,
    wallet_queue_position: Math.max(0, Number(payload?.wallet_queue_position || 0) || 0),
    wallet_queue_size: Math.max(0, Number(payload?.wallet_queue_size || 0) || 0),
    estimated_wait_seconds: Math.max(0, Number(payload?.estimated_wait_seconds || 0) || 0),
    estimated_ready_at: String(payload?.estimated_ready_at || '').trim(),
    wallet_queue_assigned_at: String(payload?.wallet_queue_assigned_at || '').trim(),
    asset_balance_after: _normalizeCashierAmount(payload?.asset_balance_after ?? payload?.asset_after ?? payload?.user_asset_after ?? payload?.balance_after),
    db_verified_withdrawable_after: _normalizeCashierAmount(payload?.db_verified_withdrawable_after),
    withdraw_asset_state: String(payload?.withdraw_asset_state || '').trim(),
    withdraw_asset_tx_id: String(payload?.withdraw_asset_tx_id || '').trim(),
    user_code: String(payload?.user_code || '').trim(),
    count: Number.isFinite(count) ? count : items.length,
    limit: Number.isFinite(limit) ? limit : 0,
    items,
    raw: payload,
  };
}

function _normalizeCashierResultPayload(payload = {}) {
  const data = payload && typeof payload === 'object' ? payload : {};
  return {
    ...data,
    request_id: String(data?.request_id || '').trim(),
    bridge_id: String(data?.bridge_id || '').trim(),
    request_type: _safeLower(data?.request_type),
    status: _safeLower(data?.status),
    event_type: _safeLower(data?.event_type),
    result: _safeUpper(data?.result),
    raw_status: _safeUpper(data?.raw_status || data?.bridge_status || data?.status),
    raw_result: _safeUpper(data?.raw_result || data?.bridge_result || data?.result),
    raw_wallet_status: String(data?.raw_wallet_status || data?.wallet_status || '').trim(),
    raw_wallet_reason: String(data?.raw_wallet_reason || data?.wallet_reason || data?.error || '').trim(),
    bridge_error_code: String(data?.bridge_error_code || data?.error_code || '').trim(),
    bridge_error_stage: String(data?.bridge_error_stage || data?.error_stage || data?.wallet_stage || '').trim(),
    reason: String(data?.reason || data?.raw_wallet_reason || data?.wallet_reason || data?.error || '').trim(),
    code: String(data?.code || data?.error_code || data?.bridge_error_code || '').trim(),
    error_code: String(data?.error_code || data?.code || data?.bridge_error_code || '').trim(),
    manual_review_required: data?.manual_review_required === true,
    wallet_queue_waiting: data?.wallet_queue_waiting === true,
    wallet_queue_position: Math.max(0, Number(data?.wallet_queue_position || 0) || 0),
    wallet_queue_size: Math.max(0, Number(data?.wallet_queue_size || 0) || 0),
    estimated_wait_seconds: Math.max(0, Number(data?.estimated_wait_seconds || 0) || 0),
    estimated_ready_at: String(data?.estimated_ready_at || '').trim(),
    wallet_queue_assigned_at: String(data?.wallet_queue_assigned_at || '').trim(),
    display_status: _safeLower(data?.display_status),
    deposit_wallet_address: String(data?.deposit_wallet_address || '').trim(),
    bridge_wallet_address: String(data?.bridge_wallet_address || '').trim(),
    wallet_address: String(data?.wallet_address || '').trim(),
    display_action: _safeUpper(data?.display_action),
    request_display: _safeUpper(data?.request_display),
    network_code: _safeUpper(data?.network_code),
    network_name: _safeLower(data?.network_name || data?.networkName || data?.network),
    asset_code: _safeUpper(data?.asset_code),
    amount: _normalizeCashierAmount(data?.amount),
    message: String(data?.message || '').trim(),
    asset_balance_after: _normalizeCashierAmount(data?.asset_balance_after ?? data?.asset_after ?? data?.user_asset_after ?? data?.balance_after),
    db_verified_withdrawable_after: _normalizeCashierAmount(data?.db_verified_withdrawable_after),
    withdraw_asset_state: String(data?.withdraw_asset_state || '').trim(),
    withdraw_asset_tx_id: String(data?.withdraw_asset_tx_id || '').trim(),
    tx_hash: String(data?.tx_hash || '').trim(),
    tx_id: String(data?.tx_id || '').trim(),
    created_at: String(data?.created_at || '').trim(),
    updated_at: String(data?.updated_at || '').trim(),
    completed_at: String(data?.completed_at || '').trim(),
  };
}

function _bindCashierSocketOnce() {
  if (__cashierSocketBound) return;
  __cashierSocketBound = true;

  cashierSocket.on('connect', () => {
    console.log('[cashierSocket] 🔗 connected:', cashierSocket.id, CASHIER_SOCKET_URL);
    _syncCashierSocketContext();
  });
  cashierSocket.on('connect_error', (err) => {
    console.error('[cashierSocket] ❌ connect_error:', err);
  });
  cashierSocket.on('disconnect', (reason) => {
    console.warn('[cashierSocket] 🔌 disconnect:', reason);
  });

  cashierSocket.on(EVENTS.CASHIER_RESULT_UPDATE, (payload = {}) => {
    const normalized = _normalizeCashierResultPayload(payload);
    console.log('[cashierSocket][cashier:result:update] ⬅', normalized);
    _dispatchCashierCustomEvent(CASHIER_RESULT_EVENT, normalized);
  });

  cashierSocket.on(EVENTS.CASHIER_STATUS_UPDATE, (payload = {}) => {
    const normalized = _normalizeCashierResultPayload(payload);
    console.log('[cashierSocket][cashier:status:update] ⬅', normalized);
    _dispatchCashierCustomEvent(CASHIER_STATUS_EVENT, normalized);
  });
}

export async function ensureCashierSocketReady() {
  _syncCashierSocketContext();
  _bindCashierSocketOnce();

  if (cashierSocket.connected) {
    return cashierSocket;
  }
  if (ensureCashierPromise) {
    return ensureCashierPromise;
  }

  ensureCashierPromise = new Promise((resolve, reject) => {
    const cleanup = () => {
      try { cashierSocket.off('connect', onConnect); } catch (_) {}
      try { cashierSocket.off('connect_error', onError); } catch (_) {}
      ensureCashierPromise = null;
    };
    const onConnect = () => {
      cleanup();
      resolve(cashierSocket);
    };
    const onError = (error) => {
      cleanup();
      reject(error);
    };

    cashierSocket.once('connect', onConnect);
    cashierSocket.once('connect_error', onError);
    try {
      cashierSocket.connect();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });

  return ensureCashierPromise;
}

function _emitWithAck(eventName, payload = {}, { timeoutMs = 10000, fallbackType = '' } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => {
      finish({
        ok: false,
        duplicated: false,
        request_id: '',
        bridge_id: '',
        request_type: _safeLower(fallbackType),
        error_code: 'CLIENT_ACK_TIMEOUT',
        message: '',
        raw: null,
      });
    }, Number(timeoutMs || 10000));

    try {
      cashierSocket.emit(eventName, payload, (ack) => {
        clearTimeout(timer);
        finish(_normalizeCashierAck(ack, fallbackType));
      });
    } catch (error) {
      clearTimeout(timer);
      finish({
        ok: false,
        duplicated: false,
        request_id: '',
        bridge_id: '',
        request_type: _safeLower(fallbackType),
        error_code: 'CLIENT_EMIT_FAILED',
        message: '',
        raw: error,
      });
    }
  });
}

export async function emitCashierDepositRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  const normalized = _normalizeCashierRequestPayload(payload, 'deposit');
  return _emitWithAck(EVENTS.CASHIER_DEPOSIT_REQUEST, normalized, {
    timeoutMs: options?.timeoutMs,
    fallbackType: 'deposit',
  });
}

export async function emitCashierWithdrawRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  const normalized = _normalizeCashierRequestPayload(payload, 'withdraw');
  return _emitWithAck(EVENTS.CASHIER_WITHDRAW_REQUEST, normalized, {
    timeoutMs: options?.timeoutMs,
    fallbackType: 'withdraw',
  });
}

export async function emitCashierHistoryRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  const normalizedType = String(payload?.request_type || payload?.requestType || '').trim().toLowerCase();
  const limit = Math.max(1, Math.min(Number(payload?.limit || 100) || 100, 100));
  return _emitWithAck(CASHIER_HISTORY_REQUEST_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_type: normalizedType,
    limit,
  }, {
    timeoutMs: options?.timeoutMs,
    fallbackType: normalizedType || 'history',
  });
}

export async function emitCashierCancelRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_CANCEL_REQUEST_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    bridge_id: String(payload?.bridge_id || payload?.bridgeId || '').trim() || undefined,
    request_type: String(payload?.request_type || payload?.requestType || '').trim().toLowerCase() || undefined,
  }, {
    timeoutMs: options?.timeoutMs,
    fallbackType: 'cancel',
  });
}

export async function emitCashierRecheckRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_RECHECK_REQUEST_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    bridge_id: String(payload?.bridge_id || payload?.bridgeId || '').trim() || undefined,
    request_type: String(payload?.request_type || payload?.requestType || 'deposit').trim().toLowerCase() || 'deposit',
  }, {
    timeoutMs: options?.timeoutMs,
    fallbackType: 'recheck',
  });
}

export async function emitCashierDepositSessionExtend(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_DEPOSIT_SESSION_EXTEND_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    bridge_id: String(payload?.bridge_id || payload?.bridgeId || '').trim() || undefined,
  }, { timeoutMs: options?.timeoutMs, fallbackType: 'deposit' });
}

export async function emitCashierDepositFinalizePartial(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_DEPOSIT_FINALIZE_PARTIAL_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    bridge_id: String(payload?.bridge_id || payload?.bridgeId || '').trim() || undefined,
  }, { timeoutMs: options?.timeoutMs, fallbackType: 'deposit' });
}

export async function emitCashierDepositRefundRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_DEPOSIT_REFUND_REQUEST_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    bridge_id: String(payload?.bridge_id || payload?.bridgeId || '').trim() || undefined,
    refund_address: String(payload?.refund_address || payload?.refundAddress || '').trim(),
  }, { timeoutMs: options?.timeoutMs, fallbackType: 'deposit' });
}

export async function emitCashierHistoryDeleteRequest(payload = {}, options = {}) {
  await ensureCashierSocketReady();
  return _emitWithAck(CASHIER_HISTORY_DELETE_REQUEST_EVENT, {
    user_code: String(payload?.user_code || payload?.userCode || '').trim(),
    request_id: String(payload?.request_id || payload?.requestId || '').trim(),
    request_type: String(payload?.request_type || payload?.requestType || '').trim().toLowerCase() || undefined,
  }, {
    timeoutMs: options?.timeoutMs,
    fallbackType: 'history_delete',
  });
}

export function onCashierResultUpdate(cb) {
  const handler = (event) => cb?.(event?.detail || {});
  try {
    window.addEventListener(CASHIER_RESULT_EVENT, handler);
  } catch (_) {}
  ensureCashierSocketReady().catch(() => {});
  return () => {
    try {
      window.removeEventListener(CASHIER_RESULT_EVENT, handler);
    } catch (_) {}
  };
}

export function onCashierStatusUpdate(cb) {
  const handler = (event) => cb?.(event?.detail || {});
  try {
    window.addEventListener(CASHIER_STATUS_EVENT, handler);
  } catch (_) {}
  ensureCashierSocketReady().catch(() => {});
  return () => {
    try {
      window.removeEventListener(CASHIER_STATUS_EVENT, handler);
    } catch (_) {}
  };
}


function _normalizePaymentAddressesAck(ack = {}) {
  const payload = ack && typeof ack === 'object' ? ack : {};
  const rawList = Array.isArray(payload?.addresses)
    ? payload.addresses
    : String(payload?.csv || payload?.crypto_address || '').split(',');
  const seen = new Set();
  const addresses = [];
  for (const raw of rawList) {
    const address = String(raw || '').trim().replace(/,/g, '');
    if (!address || seen.has(address)) continue;
    seen.add(address);
    addresses.push(address);
  }
  return {
    success: payload?.success === true,
    error: String(payload?.error || '').trim(),
    user_code: String(payload?.user_code || '').trim(),
    csv: addresses.join(','),
    addresses,
    raw: payload,
  };
}

function _syncPaymentAddressesToLocalUser(addresses = []) {
  try {
    const list = Array.isArray(addresses) ? addresses : [];
    const raw = localStorage.getItem('user') || '{}';
    const user = JSON.parse(raw);
    const next = {
      ...(user && typeof user === 'object' ? user : {}),
      crypto_address: list.join(','),
      payment_addresses: list,
    };
    localStorage.setItem('user', JSON.stringify(next));
  } catch (_) {}
}

export async function fetchPaymentAddresses(payload = {}, options = {}) {
  await ensureRootNSReady();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => {
      finish({ success: false, error: 'payment_address_list_timeout', addresses: [], csv: '' });
    }, Number(options?.timeoutMs || 8000));

    try {
      rootSocket.emit(PAYMENT_ADDRESS_LIST_EVENT, {
        user_code: String(payload?.user_code || userCode || '').trim(),
      }, (ack) => {
        clearTimeout(timer);
        const normalized = _normalizePaymentAddressesAck(ack);
        if (normalized.success) _syncPaymentAddressesToLocalUser(normalized.addresses);
        finish(normalized);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: String(error?.message || 'payment_address_list_emit_failed').trim(), addresses: [], csv: '' });
    }
  });
}

export async function savePaymentAddresses(addresses = [], payload = {}, options = {}) {
  await ensureRootNSReady();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => {
      finish({ success: false, error: 'payment_address_save_timeout', addresses: [], csv: '' });
    }, Number(options?.timeoutMs || 8000));

    try {
      rootSocket.emit(PAYMENT_ADDRESS_SAVE_EVENT, {
        user_code: String(payload?.user_code || userCode || '').trim(),
        addresses: Array.isArray(addresses) ? addresses : [],
      }, (ack) => {
        clearTimeout(timer);
        const normalized = _normalizePaymentAddressesAck(ack);
        if (normalized.success) _syncPaymentAddressesToLocalUser(normalized.addresses);
        finish(normalized);
      });
    } catch (error) {
      clearTimeout(timer);
      finish({ success: false, error: String(error?.message || 'payment_address_save_emit_failed').trim(), addresses: [], csv: '' });
    }
  });
}

export function offAllCashierListeners() {
  console.log('[cashierSocket] removeAllListeners()');
  cashierSocket.removeAllListeners();
}

/* =========================================================
 * 기타
 * =======================================================*/
export function offAllRootListeners() {
  rootSocketDebug("[rootSocket] removeAllListeners()");
  rootSocket.removeAllListeners();
}
